import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import watiLogo from "../assets/wati_logo_only.png";
import axios from "axios";
import dayjs from "dayjs";
import { DatePicker, Badge, Spin, Button, Card, Tag, Modal,  message, notification, Collapse, Timeline, Switch, Avatar, Tooltip, Table, Image, Dropdown, Input, Select, Drawer, Empty, Radio, TimePicker, Checkbox } from "antd";
import { ReloadOutlined, CalendarOutlined, UserOutlined, PhoneOutlined, DollarOutlined, ClockCircleOutlined, TagOutlined, HourglassOutlined, CheckCircleOutlined, CarOutlined, CloseCircleOutlined, RollbackOutlined, SortDescendingOutlined, WhatsAppOutlined, PictureOutlined, ShopOutlined, GlobalOutlined, FireOutlined, CopyOutlined, CheckOutlined, PlusOutlined, DeleteOutlined, EditOutlined, PushpinOutlined, PushpinFilled, SendOutlined, WechatOutlined, SearchOutlined, AudioOutlined, AudioMutedOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, FileExcelOutlined, FileZipOutlined, FileTextOutlined, PlayCircleOutlined, PauseCircleOutlined, DownloadOutlined, NotificationOutlined, StarFilled, StarOutlined, QuestionCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, EyeOutlined, PaperClipOutlined } from "@ant-design/icons";
import { DirhamSymbol } from "dirham/react";
import { API_DOMAIN, country_codes } from "../components/constants";
import { v4 as uuidv4 } from "uuid";
import { getCountryCode } from "../components/misc_functions";
import { AuthContext } from "../components/contexts/AuthContext";
import { useWatiSocket } from "../components/contexts/WatiSocketContext";
import { motion, AnimatePresence } from "framer-motion";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";

const { RangePicker } = DatePicker;

const CHAT_API              = "https://srm.uzdpart.com:33123/wati_messages/fetch_messages";
const CHAT_MESSAGES_API     = "https://srm.uzdpart.com:33123/wati_messages/fetch_specific_chat_messages";
const SEND_VOICE_API        = "https://srm.uzdpart.com:33123/wati_messages/send_voice_message";
const SEND_IMAGE_API        = "https://srm.uzdpart.com:33123/wati_messages/send_image_message";
const SEND_VIDEO_API        = "https://srm.uzdpart.com:33123/wati_messages/send_video_message";
const SEND_DOCUMENT_API     = "https://srm.uzdpart.com:33123/wati_messages/send_document_message";
const SEND_TEXT_API         = "https://srm.uzdpart.com:33123/wati_messages/send_text_message";
const MARK_AS_READ_API      = "https://srm.uzdpart.com:33123/wati_messages/mark_chat_as_read";

const MB = 1024 * 1024;
const KB = 1024;
const WATI_FILE_RULES = {
  // Images — 5 MB
  "image/jpeg": { category: "image",    maxBytes: 5   * MB, label: "JPEG"        },
  "image/png":  { category: "image",    maxBytes: 5   * MB, label: "PNG"         },
  // Videos — 16 MB
  "video/mp4":  { category: "video",    maxBytes: 16  * MB, label: "MP4"         },
  "video/3gpp": { category: "video",    maxBytes: 16  * MB, label: "3GP"         },
  // Audio — 16 MB
  "audio/aac":  { category: "audio",    maxBytes: 16  * MB, label: "AAC"         },
  "audio/mp4":  { category: "audio",    maxBytes: 16  * MB, label: "MP4 Audio"   },
  "audio/mpeg": { category: "audio",    maxBytes: 16  * MB, label: "MP3"         },
  "audio/amr":  { category: "audio",    maxBytes: 16  * MB, label: "AMR"         },
  "audio/ogg":  { category: "audio",    maxBytes: 16  * MB, label: "OGG"         },
  // Stickers — 100 KB, WebP only
  "image/webp": { category: "sticker",  maxBytes: 100 * KB, label: "WebP Sticker"},
  // Documents — 100 MB
  "application/pdf":  { category: "document", maxBytes: 100 * MB, label: "PDF"   },
  "application/msword":                                                         { category: "document", maxBytes: 100 * MB, label: "DOC"  },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":   { category: "document", maxBytes: 100 * MB, label: "DOCX" },
  "application/vnd.ms-powerpoint":                                             { category: "document", maxBytes: 100 * MB, label: "PPT"  },
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": { category: "document", maxBytes: 100 * MB, label: "PPTX" },
  "application/vnd.ms-excel":                                                  { category: "document", maxBytes: 100 * MB, label: "XLS"  },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":         { category: "document", maxBytes: 100 * MB, label: "XLSX" },
  "text/plain":       { category: "document", maxBytes: 100 * MB, label: "TXT"   },
  "application/zip":  { category: "document", maxBytes: 100 * MB, label: "ZIP"   },
};
const WATI_ACCEPT_ATTR  = Object.keys(WATI_FILE_RULES).join(",");
const WATI_ACCEPT_LABEL = "Images: JPEG, PNG (5 MB) · Videos: MP4, 3GP (16 MB) · Audio: AAC, MP3, AMR, OGG (16 MB) · Stickers: WebP 512×512 (100 KB) · Docs: PDF, Word, Excel, PowerPoint, TXT, ZIP (100 MB)";

const TICKET_STATUS = {
  0: { label: "Unassigned", color: "#cf1322", bg: "#fff1f0", border: "#ff4d4f" },
  1: { label: "Open",       color: "#389e0d", bg: "#f6ffed", border: "#52c41a" },
  2: { label: "Solved",     color: "#0958d9", bg: "#e6f4ff", border: "#91caff" },
  3: { label: "Pending",    color: "#d46b08", bg: "#fff7e6", border: "#ffa940" },
  4: { label: "Expired",    color: "#d48806", bg: "#fffbe6", border: "#ffe58f" },
};

function mapChatItem(item) {
  const t = (item.messageType || "").toLowerCase();
  let lastMsg = item.text || null;
  if      (t === "audio")    lastMsg = "Voice message";
  else if (t === "image")    lastMsg = item.caption || "Photo";
  else if (t === "video")    lastMsg = item.caption || "Video";
  else if (t === "document") lastMsg = item.caption || item.fileName || "Document";
  return {
    id:               item.id,
    conversationId:   item.ticket?.conversationId || item.id,
    customer_name:    item.name || null,
    phone:            item.whatsappId || null,
    last_message:     lastMsg,
    last_message_time: item.lastUpdated ? dayjs(parseFloat(item.lastUpdated) * 1000) : null,
    agent:            item.ticket?.assignedFullName || null,
    unread_count:     item.unreadCount || 0,
    customer_type:    item.type ?? null,
    ticket_status:    item.ticket?.status ?? null,
    ticket_type:      item.activeTicketType || null,
    topic:            item.ticket?.topicName || null,
    starred:          item.starred || item.isFavorite || false,
    expiry:           (() => {
                        if (item.remainingTime) {
                          const [h, m] = item.remainingTime.split(":").map(Number);
                          if (!isNaN(h) && !isNaN(m)) return dayjs().add(h, "hour").add(m, "minute");
                        }
                        if (item.lastIncomingDate) return dayjs(item.lastIncomingDate).add(24, "hour");
                        return null;
                      })(),
    raw:              item,
  };
}

function expiryDisplay(expiry) {
  if (!expiry?.isValid()) return null;
  const mins = expiry.diff(dayjs(), "minute");
  if (mins <= 0) return { label: "Expired", color: "#ff4d4f" };
  if (mins < 60) return { label: `${mins}m`, color: "#ff4d4f" };
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs < 6) return { label: rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`, color: "#fa8c16" };
  return { label: `${hrs}h`, color: "#8c8c8c" };
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function CopyablePhone({ phone }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(phone).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
      {phone}
      <span onClick={handleCopy} style={{ cursor: "pointer", color: copied ? "#52c41a" : "#8c8c8c" }}>
        {copied ? <CheckOutlined style={{ fontSize: 10 }} /> : <CopyOutlined style={{ fontSize: 10 }} />}
      </span>
    </span>
  );
}

function isInternational(q) {
  const phone = ((q.phone || q.whatsappId || "")).replace(/\D/g, "");
  return phone.length > 0 && !phone.startsWith("971") && !phone.startsWith("0");
}

function getCardCountry(phone) {
  if (!phone) return null;
  try {
    const code = getCountryCode(phone);
    const name = country_codes[code];
    return name ? { code, name } : null;
  } catch { return null; }
}


const AGENT_PALETTE = ["#1677ff","#13c2c2","#2f54eb","#d48806","#389e0d","#7cb305","#c41d7f","#d46b08","#531dab","#d4380d"];
function getAgentColor(name) {
  if (!name) return "#8c8c8c";
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AGENT_PALETTE[h % AGENT_PALETTE.length];
}
function agentInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : name[0].toUpperCase();
}

// ── WhatsApp text formatting ─────────────────────────────────────────────────
const URL_REGEX = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

function _fmtSegment(str) {
  const safe = str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return safe
    .replace(/\*([^*\n]+)\*/g, "<strong>$1</strong>")
    .replace(/_([^_\n]+)_/g, "<em>$1</em>")
    .replace(/~([^~\n]+)~/g, "<s>$1</s>")
    .replace(/`([^`\n]+)`/g, '<code style="background:#f5f5f5;padding:1px 4px;border-radius:3px;font-family:monospace;font-size:11px">$1</code>');
}

function WaText({ text, onLinkClick }) {
  if (!text) return null;
  const parts = [];
  let last = 0;
  let m;
  URL_REGEX.lastIndex = 0;
  while ((m = URL_REGEX.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    parts.push({ type: "link", value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });

  return (
    <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {parts.map((p, i) =>
        p.type === "link" ? (
          <a key={i}
            href={p.value}
            onClick={e => { e.preventDefault(); onLinkClick ? onLinkClick(p.value) : window.open(p.value.startsWith("http") ? p.value : `https://${p.value}`, "_blank", "noopener,noreferrer"); }}
            style={{ color: "#00a884", textDecoration: "underline", cursor: "pointer" }}>
            {p.value}
          </a>
        ) : (
          <span key={i} dangerouslySetInnerHTML={{ __html: _fmtSegment(p.value) }} />
        )
      )}
    </span>
  );
}

function formatWaText(text) { return <WaText text={text} />; }

(() => {
  if (typeof document !== "undefined" && !document.getElementById("msg-status-glow-style")) {
    const s = document.createElement("style");
    s.id = "msg-status-glow-style";
    s.textContent = `
      @keyframes msgStatusGlow {
        0%   { filter: drop-shadow(0 0 0px currentColor); opacity: 1; }
        35%  { filter: drop-shadow(0 0 6px currentColor); opacity: 0.8; }
        100% { filter: drop-shadow(0 0 0px currentColor); opacity: 1; }
      }
      .cpt, .cpt * {
        transition:
          background-color 0.35s ease,
          background 0.35s ease,
          color 0.25s ease,
          border-color 0.35s ease !important;
      }
      @keyframes chatInputLaunch {
        0%   { transform: scale(1)    translateY(0);   opacity: 1; }
        40%  { transform: scale(0.96) translateY(-7px); opacity: 0.5; }
        100% { transform: scale(1)    translateY(0);   opacity: 1; }
      }
      .chat-input-launch {
        animation: chatInputLaunch 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
    `;
    document.head.appendChild(s);
  }
})();

function MsgStatus({ status }) {
  const prevRef = React.useRef(status);
  const [glowing, setGlowing] = React.useState(false);

  React.useEffect(() => {
    if (prevRef.current !== status) {
      prevRef.current = status;
      setGlowing(true);
      const t = setTimeout(() => setGlowing(false), 600);
      return () => clearTimeout(t);
    }
  }, [status]);

  if (!status) return null;

  const glowStyle = glowing
    ? { animation: "msgStatusGlow 0.6s ease-out", display: "inline-flex", alignItems: "center" }
    : { display: "inline-flex", alignItems: "center" };

  if (status === "READ")      return <span style={{ ...glowStyle, color: "#4fc3f7", fontSize: 11, marginLeft: 3 }}>✓✓</span>;
  if (status === "DELIVERED") return <span style={{ ...glowStyle, color: "#8c8c8c", fontSize: 11, marginLeft: 3 }}>✓✓</span>;
  if (status === "SENT")      return <span style={{ ...glowStyle, color: "#8c8c8c", fontSize: 11, marginLeft: 3 }}>✓</span>;
  if (status === "SENDING")   return <ClockCircleOutlined style={{ ...glowStyle, color: "#bfbfbf", fontSize: 10, marginLeft: 3 }} />;
  if (status === "FAILED")    return <span style={{ ...glowStyle, color: "#ff4d4f", fontSize: 11, marginLeft: 3 }}>✗</span>;
  return null;
}

// Context to pass the messages scroll container to MsgMediaContent
const MediaScrollCtx = React.createContext(null);

// Chat panel theme — true = dark (WhatsApp-style), false = light
const ChatThemeCtx = React.createContext(true);

function ct(isDark) {
  return isDark ? {
    bg:        "#0b141a",
    surface:   "#1f2c34",
    border:    "#2a3942",
    pill:      "#182229",
    bubbleIn:  "#202c33",
    bubbleOut: "#005c4b",
    text:      "#e9edef",
    textSub:   "#8696a0",
    input:     "#2a3942",
    bgFilter:  "brightness(0.15) saturate(0.4)",
    bgOpacity: 0.6,
  } : {
    bg:        "#eae6df",
    surface:   "#f0f2f5",
    border:    "#d1d7db",
    pill:      "#dfe5e7",
    bubbleIn:  "#ffffff",
    bubbleOut: "#d9fdd3",
    text:      "#111b21",
    textSub:   "#667781",
    input:     "#ffffff",
    bgFilter:  "brightness(0.4) saturate(0.5)",
    bgOpacity: 0.12,
  };
}

// Signed URL cache: rawUrl -> { signedUrl, expiresAt (ms) }
const _mediaCache = new Map();

function _extractSignedUrlExpiry(url) {
  try {
    const u = new URL(url);
    // AWS v4: X-Amz-Date + X-Amz-Expires
    const amzDate    = u.searchParams.get("X-Amz-Date");
    const amzExpires = u.searchParams.get("X-Amz-Expires");
    if (amzDate && amzExpires) {
      const start = dayjs(amzDate, "YYYYMMDDTHHmmss[Z]");
      return start.add(parseInt(amzExpires), "second").valueOf();
    }
    // GCS: X-Goog-Date + X-Goog-Expires
    const googDate    = u.searchParams.get("X-Goog-Date");
    const googExpires = u.searchParams.get("X-Goog-Expires");
    if (googDate && googExpires) {
      const start = dayjs(googDate, "YYYYMMDDTHHmmss[Z]");
      return start.add(parseInt(googExpires), "second").valueOf();
    }
    // Generic Unix: Expires=<seconds>
    const expires = u.searchParams.get("Expires");
    if (expires) return parseInt(expires) * 1000;
    // Azure SAS: se=<ISO>
    const se = u.searchParams.get("se");
    if (se) return new Date(se).getTime();
  } catch {}
  return Date.now() + 3600 * 1000; // fallback: 1 hour
}

function _resolveMediaUrl(rawUrl) {
  const cached = _mediaCache.get(rawUrl);
  if (cached && cached.expiresAt > Date.now() + 60_000) return Promise.resolve(cached.signedUrl);
  return axios.post(
    API_DOMAIN + "/wati_messages/parse_media_file_url",
    { file_name: rawUrl },
    { headers: { accessToken: localStorage.getItem("accessToken") } }
  ).then(res => {
    if (res.data?.success && res.data?.data) {
      const signedUrl = res.data.data;
      _mediaCache.set(rawUrl, { signedUrl, expiresAt: _extractSignedUrlExpiry(signedUrl) });
      return signedUrl;
    }
    return null;
  }).catch(() => null);
}

function AudioPlayer({ src }) {
  const isDark     = React.useContext(ChatThemeCtx);
  const audioRef   = useRef(null);
  const [playing,     setPlaying]     = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration,    setDuration]    = useState(0);
  const [speed,       setSpeed]       = useState(1);
  const SPEEDS = [1, 1.5, 2];

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else         { a.play();  setPlaying(true);  }
  };

  const cycleSpeed = () => {
    const a    = audioRef.current;
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (a) a.playbackRate = next;
  };

  const handleSeek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = pct * duration;
    setCurrentTime(pct * duration);
  };

  const fmt = s => {
    if (!s || isNaN(s)) return "0:00";
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
  };

  const pct      = duration ? (currentTime / duration) * 100 : 0;
  const iconClr  = isDark ? "#e9edef" : "#111b21";
  const trackBg  = isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)";
  const subClr   = isDark ? "#8696a0" : "#667781";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, width: 210 }}>
      <audio ref={audioRef} src={src}
        onTimeUpdate={e  => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={e => setDuration(e.target.duration)}
        onEnded={() => { setPlaying(false); setCurrentTime(0); }}
      />
      <Button type="text" size="small" icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
        onClick={togglePlay}
        style={{ color: iconClr, flexShrink: 0, padding: 0, fontSize: 20, width: 28, height: 28 }}
      />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
        <div onClick={handleSeek}
          style={{ height: 3, background: trackBg, borderRadius: 2, cursor: "pointer", position: "relative" }}>
          <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${pct}%`, background: "#25d366", borderRadius: 2 }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontSize: 9, color: subClr }}>{fmt(currentTime)}</span>
          <span style={{ fontSize: 9, color: subClr }}>{fmt(duration)}</span>
        </div>
      </div>
      <Button type="text" size="small" onClick={cycleSpeed}
        style={{ color: subClr, fontSize: 10, fontWeight: 700, flexShrink: 0, padding: "0 3px", minWidth: 26, height: 22 }}>
        {speed}x
      </Button>
    </div>
  );
}

function MsgMediaContent({ item }) {
  const type      = (item.type || "").toLowerCase();
  const rawUrl    = item.data || null;
  const localUrl  = item._localPreviewUrl || null; // blob URL for optimistic file bubbles
  const scrollCtx = useContext(MediaScrollCtx);

  const cachedEntry = rawUrl && !localUrl ? _mediaCache.get(rawUrl) : null;
  const initialUrl  = localUrl || (cachedEntry && cachedEntry.expiresAt > Date.now() + 60_000 ? cachedEntry.signedUrl : null);

  const [signedUrl, setSignedUrl]     = useState(initialUrl);
  const [loading, setLoading]         = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [videoOpen, setVideoOpen]     = useState(false);
  const containerRef                  = useRef(null);

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!rawUrl || downloading) return;
    setDownloading(true);
    axios.post(
      API_DOMAIN + "/wati_messages/download_wati_media_file",
      { file_name: rawUrl },
      { headers: { accessToken: localStorage.getItem("accessToken") }, responseType: "blob" }
    ).then(res => {
      const contentDisposition = res.headers["content-disposition"] || "";
      const nameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/i);
      const fileName  = nameMatch?.[1] || item.fileName || rawUrl.split("/").pop() || "download";
      const blobUrl   = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = fileName;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(blobUrl);
    }).catch(() => message.error("Download failed."))
      .finally(() => setDownloading(false));
  };

  useEffect(() => {
    if (!rawUrl || signedUrl || localUrl) return;
    const el   = containerRef.current;
    const root = scrollCtx?.current || null;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      setLoading(true);
      _resolveMediaUrl(rawUrl).then(url => { setSignedUrl(url); setLoading(false); });
    }, { root, threshold: 0, rootMargin: "120px 0px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [rawUrl, signedUrl]); // eslint-disable-line

  const spinner = <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 48 }}><Spin size="small" /></div>;

  const dlBtn = signedUrl && (
    <span onClick={handleDownload}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: "50%", background: "rgba(0,0,0,0.45)", color: "#fff", flexShrink: 0, cursor: downloading ? "not-allowed" : "pointer" }}>
      {downloading ? <Spin size="small" style={{ fontSize: 11 }} /> : <DownloadOutlined style={{ fontSize: 13 }} />}
    </span>
  );

  const caption = item.caption || item.text || null;
  const captionEl = caption ? <div style={{ fontSize: 12, marginTop: 4 }}>{formatWaText(caption)}</div> : null;

  if (type === "image") return (
    <div ref={containerRef} style={{ display: "inline-block", maxWidth: "100%" }}>
      {loading ? <div style={{ width: 180, height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div> : signedUrl
        ? <div style={{ position: "relative", display: "inline-block", maxWidth: "100%", cursor: "pointer" }}>
            <Image src={signedUrl} alt="Photo"
              style={{ height: 180, width: "auto", maxWidth: "100%", borderRadius: 8, display: "block" }}
              preview={{ mask: false }}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==" />
            <div style={{ position: "absolute", top: 6, right: 6 }}>{dlBtn}</div>
          </div>
        : <div style={{ width: 180, height: 180, background: "rgba(0,0,0,0.06)", borderRadius: 8, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#8c8c8c", cursor: "pointer" }}><PictureOutlined style={{ fontSize: 28 }} /><div style={{ fontSize: 11, marginTop: 4 }}>Photo</div></div>
      }
      {captionEl}
    </div>
  );
  if (type === "video") return (
    <div ref={containerRef} style={{ display: "inline-block", maxWidth: "100%" }}>
      {loading ? <div style={{ width: 180, height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div> : signedUrl
        ? <>
            <div onClick={() => setVideoOpen(true)}
              style={{ position: "relative", display: "inline-block", maxWidth: "100%", cursor: "pointer", borderRadius: 8, overflow: "hidden" }}>
              <video src={signedUrl} preload="metadata"
                style={{ display: "block", height: 180, width: "auto", maxWidth: "100%", borderRadius: 8, pointerEvents: "none" }} />
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.3)" }}>
                <PlayCircleOutlined style={{ fontSize: 40, color: "#fff" }} />
              </div>
              <div style={{ position: "absolute", top: 6, right: 6 }} onClick={e => e.stopPropagation()}>{dlBtn}</div>
            </div>
            <Modal open={videoOpen} onCancel={() => setVideoOpen(false)} footer={null} centered
              width={Math.min(window.innerWidth * 0.88, 900)}
              styles={{ body: { padding: 0, background: "#000", borderRadius: 8, overflow: "hidden" }, mask: { backdropFilter: "blur(4px)" } }}
              closable={false} destroyOnClose>
              {videoOpen && (
                <video src={signedUrl} controls autoPlay
                  style={{ width: "100%", maxHeight: "75vh", display: "block", outline: "none" }} />
              )}
            </Modal>
          </>
        : <div style={{ width: 180, height: 180, background: "rgba(0,0,0,0.75)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PlayCircleOutlined style={{ fontSize: 36, color: "#fff", opacity: 0.85 }} />
          </div>
      }
      {captionEl}
    </div>
  );
  if (type === "audio") return (
    <div ref={containerRef}>
      {loading ? spinner : signedUrl
        ? <AudioPlayer src={signedUrl} />
        : <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px" }}>
            <AudioOutlined style={{ fontSize: 18, color: "#8c8c8c" }} />
            <span style={{ fontSize: 12, color: "#595959" }}>Voice message</span>
          </div>
      }
      {captionEl}
    </div>
  );
  if (type === "document") return (
    <div ref={containerRef}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px", height: 44, cursor: "pointer" }}>
        <FileOutlined style={{ fontSize: 20, color: "#8c8c8c", flexShrink: 0 }} />
        <span style={{ fontSize: 12, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.fileName || "Document"}</span>
        {loading ? <Spin size="small" /> : dlBtn}
      </div>
      {captionEl}
    </div>
  );
  if (type === "sticker") return (
    <div ref={containerRef} style={{ display: "inline-block" }}>
      {loading
        ? <div style={{ width: 100, height: 100, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div>
        : signedUrl
          ? <Image src={signedUrl} alt="Sticker"
              style={{ width: 100, height: 100, objectFit: "contain", display: "block" }}
              preview={{ mask: false }} />
          : <div style={{ width: 100, height: 100, display: "flex", alignItems: "center", justifyContent: "center", color: "#8c8c8c", fontSize: 11 }}>Sticker</div>
      }
    </div>
  );
  return null;
}

// ── Active Chat Card ──────────────────────────────────────────────────────────
function ActiveChatCard({ chat, isSelected, onClick }) {
  const customerType = chat?.customer_type;
  const avatarIcon   = customerType === 0 ? <ShopOutlined /> : customerType === 1 ? <UserOutlined /> : <QuestionCircleOutlined />;
  const avatarColor  = customerType === 0 ? "#1677ff" : customerType === 1 ? "#722ed1" : "#d48806";
  const typeLabel    = customerType === 0 ? "B2B" : customerType === 1 ? "B2C" : null;
  const typeColor    = customerType === 0 ? "blue" : "purple";
  const tsCfg        = chat.ticket_status != null ? TICKET_STATUS[chat.ticket_status] : null;
  const expiry       = expiryDisplay(chat.expiry);
  const cardCountry  = getCardCountry(chat.phone);
  const isIntl       = cardCountry && cardCountry.name !== "United Arab Emirates";
  const timeStr = chat.last_message_time?.isValid?.()
    ? (chat.last_message_time.isSame(dayjs(), "day") ? chat.last_message_time.format("hh:mm A") : fmtShortDate(chat.last_message_time))
    : null;
  return (
    <div onClick={onClick} style={{
      padding: "9px 11px", borderRadius: 8,
      border: `1.5px solid ${isSelected ? "#1677ff" : "#d0d0d0"}`,
      borderLeft: `4px solid ${isSelected ? "#1677ff" : tsCfg?.border || "#d0d0d0"}`,
      borderRight: isIntl && !isSelected ? "4px solid #1677ff" : undefined,
      backgroundColor: isSelected ? "#e6f4ff" : "#fff",
      cursor: "pointer", display: "flex", gap: 10, alignItems: "flex-start",
      boxShadow: isSelected ? "0 0 0 2px #bae0ff" : "0 1px 3px rgba(0,0,0,0.06)",
      transition: "all 0.15s",
      position: "relative",
    }}>
      {/* Globe watermark */}
      {isIntl && (
        <GlobalOutlined style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: 72, color: "rgba(22,119,255,0.14)", pointerEvents: "none", zIndex: 0 }} />
      )}
      <div style={{ position: "relative", zIndex: 1, display: "flex", gap: 10, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <Avatar size={38} icon={avatarIcon} style={{ backgroundColor: avatarColor, fontSize: 17 }} />
          {chat.unread_count > 0 && (
            <Badge count={chat.unread_count} style={{ backgroundColor: "#25d366", fontSize: 11, minWidth: 20, height: 20, lineHeight: "20px", padding: "0 5px", position: "absolute", top: -5, right: -8 }} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4, marginBottom: 2 }}>
            <span style={{ fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {chat.customer_name || chat.phone || "Unknown"}
            </span>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, gap: 2 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {chat.starred && <StarFilled style={{ fontSize: 11, color: "#faad14" }} />}
                {timeStr && <span style={{ fontSize: 10, color: "#8c8c8c", whiteSpace: "nowrap" }}>{timeStr}</span>}
              </span>
              {expiry && (
                <Tooltip title="Conversation window expiry">
                  <span style={{ fontSize: 9, color: expiry.color, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 2 }}>
                    <ClockCircleOutlined style={{ fontSize: 9 }} />{expiry.label}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
          <div style={{ fontSize: 11, color: "#595959", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 5 }}>
            {chat.last_message || <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>No messages</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, flex: 1 }}>
              {chat.agent && (
                <Tag style={{ fontSize: 10, margin: 0, padding: "0 5px", border: `1.5px solid ${getAgentColor(chat.agent)}`, color: getAgentColor(chat.agent), background: "transparent", display: "inline-flex", alignItems: "center", gap: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 110, flexShrink: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.12)" }}>
                  <Avatar size={11} style={{ backgroundColor: getAgentColor(chat.agent), fontSize: 7, flexShrink: 0 }}>{agentInitials(chat.agent)}</Avatar>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{chat.agent}</span>
                </Tag>
              )}
              {chat.topic && chat.topic.toLowerCase() !== "general enquiry" && (
                <Tag style={{ fontSize: 9, margin: 0, padding: "0 4px", lineHeight: "16px", border: "1px solid #d9d9d9", color: "#595959", background: "#fafafa", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 90, boxShadow: "0 1px 3px rgba(0,0,0,0.12)" }}>
                  {chat.topic}
                </Tag>
              )}
              {chat.ticket_type?.toLowerCase() === "ctwa" && (
                <Tag style={{ fontSize: 9, margin: 0, padding: "0 4px", lineHeight: "16px", flexShrink: 0, color: "#531dab", background: "#f9f0ff", border: "1px solid #d3adf7", fontWeight: 600, boxShadow: "0 1px 3px rgba(0,0,0,0.12)" }}>
                  CTWA
                </Tag>
              )}
              {typeLabel && <Tag color={typeColor} style={{ fontSize: 9, margin: 0, padding: "0 4px", lineHeight: "16px", flexShrink: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.12)" }}>{typeLabel}</Tag>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              {tsCfg && (
                <Tag style={{ fontSize: 9, margin: 0, padding: "0 5px", lineHeight: "16px", flexShrink: 0, color: tsCfg.color, background: tsCfg.bg, border: `1px solid ${tsCfg.border}`, fontWeight: 600, boxShadow: "0 1px 3px rgba(0,0,0,0.12)" }}>
                  {tsCfg.label}
                </Tag>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function parseAddresses(raw) {
  if (!raw) return [];
  if (typeof raw === "string") { try { return JSON.parse(raw); } catch { return []; } }
  return Array.isArray(raw) ? raw : [];
}

// ── Customer Edit Drawer ───────────────────────────────────────────────────────
const GMAPS_KEY = "AIzaSyCKT6hm3CTWMFr56LLdsXUjlZpFHY6ihUs";
const GMAPS_LIBS = ["places"];

function CustomerEditDrawer({ customer, open, onClose, onSaved }) {
  const { authState } = useContext(AuthContext);
  const [draft, setDraft]               = useState({});
  const [addresses, setAddresses]       = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [subForm, setSubForm]           = useState(null); // null=hidden, object=active
  const [vehicleSubForm, setVehicleSubForm] = useState(null);
  const [vehicles, setVehicles]         = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [vehiclesLoading, setVehiclesLoading]   = useState(false);
  const [vehiclesSaving, setVehiclesSaving]     = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [emailError, setEmailError]             = useState("");
  const [activeKeys, setActiveKeys]     = useState(["addresses"]);
  const [saving, setSaving]             = useState(false);
  const autocompleteRef                 = useRef(null);
  const subFormRef                      = useRef(null);
  const vehicleSubFormRef               = useRef(null);

  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GMAPS_KEY,
    libraries: GMAPS_LIBS,
  });

  // Re-initialise draft whenever the drawer opens
  useEffect(() => {
    if (!open || !customer) return;
    setDraft({
      customer_name:      customer.customer_name      || "",
      customer_real_name: customer.customer_real_name || "",
      customer_email:     customer.customer_email     || "",
      source:             customer.source             ?? null,
      status:             customer.status ? true : false,
      type:               customer.type               ?? null,
    });
    setAddresses(
      parseAddresses(customer.customer_addresses_new ?? customer.addresses)
        .map((a, i) => a.id != null ? a : { ...a, id: `existing-${i}` })
    );
    setSubForm(null);
    setVehicleSubForm(null);
    setEditingVehicleId(null);
    setModelOptions([]);
    setActiveKeys(["addresses"]);
    loadVehicles(customer?.customer_id);
  }, [open, customer]);

  // Fetch source options once per session
  useEffect(() => {
    if (sourceOptions.length > 0) return;
    axios.post(
      API_DOMAIN + "/whatsapp/get_misc_mode",
      { mode: "query_source" },
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    ).then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setSourceOptions(list);
    }).catch(() => {});
  }, []); // eslint-disable-line

  // Fetch brands once when drawer opens
  useEffect(() => {
    if (!open || brandOptions.length > 0) return;
    setLoadingBrands(true);
    axios.post(
      API_DOMAIN + "/whatsapp/get_makes_v2",
      { name: "" },
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    ).then(res => {
      if (res.data.success) {
        setBrandOptions(res.data.data.map(item => ({ value: item.id, label: item.name })));
      }
    }).catch(() => {}).finally(() => setLoadingBrands(false));
  }, [open]); // eslint-disable-line

  async function loadVehicleModels(brandId) {
    if (!brandId) { setModelOptions([]); return; }
    setLoadingModels(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/whatsapp/get_models_v2",
        { make: brandId },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (res.data.success) {
        setModelOptions(res.data.data.map(item => ({ value: item.id, label: item.model })));
      }
    } catch { setModelOptions([]); }
    finally { setLoadingModels(false); }
  }

  async function loadVehicles(customerId) {
    if (!customerId) { setVehicles([]); return; }
    setVehiclesLoading(true);
    try {
      const res = await axios.get(
        `${API_DOMAIN}/customer_vehicles/get_by_customer?customer_id=${customerId}`,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setVehicles(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch { setVehicles([]); }
    finally { setVehiclesLoading(false); }
  }

  const setField = (key, val) => setDraft(d => ({ ...d, [key]: val }));

  // ── Address CRUD (local state only — saved on drawer Save) ───────────────────
  const openAddSubForm = () => {
    setActiveKeys(["addresses"]);
    setSubForm({
      name:      customer?.customer_real_name?.trim() || customer?.customer_name?.trim() || "",
      number:    customer?.customer_mobile || "",
      address:   "",
      type:      "Home",
      pref_time: null,
    });
    setTimeout(() => subFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const openAddVehicleForm = () => {
    setActiveKeys(["vehicles"]);
    setVehicleSubForm({
      brand_id: null, model_id: null, variant: "", year: null,
      vin: "", engine_code: "", engine_displacement: "", engine_layout: null,
      transmission_type: null, transmission_code: "", drivetrain: null,
      fuel_type: null, country_spec: null, trim_info: "", notes: "",
      is_primary: false, status: true,
    });
    setModelOptions([]);
    setTimeout(() => vehicleSubFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const editAddress  = (addr) => setSubForm({ ...addr });
  const deleteAddress = (id)  => setAddresses(prev => prev.filter(a => a.id !== id));

  const saveSubForm = () => {
    if (!subForm?.name?.trim() || !subForm?.number?.trim() || !subForm?.address?.trim()) {
      message.warning("Name, number and address are required");
      return;
    }
    const isExisting = addresses.some(a => a.id === subForm.id);
    if (isExisting) {
      setAddresses(prev => prev.map(a => a.id === subForm.id ? subForm : a));
    } else {
      setAddresses(prev => [...prev, { ...subForm, id: Date.now() }]);
    }
    setSubForm(null);
  };

  // ── Save entire customer ────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!customer?.customer_id) return;
    if (emailError) { message.warning("Please fix the email address before saving"); return; }
    const payload = { customer_id: customer.customer_id };

    if (draft.customer_name      !== (customer.customer_name      || "")) payload.customer_name      = draft.customer_name;
    if (draft.customer_real_name !== (customer.customer_real_name || "")) payload.customer_real_name = draft.customer_real_name;
    if (draft.customer_email     !== (customer.customer_email     || "")) payload.customer_email     = draft.customer_email;
    if (draft.source             !== (customer.source             ?? null)) payload.source           = draft.source;
    if ((draft.status ? 1 : 0)   !== (customer.status            ?  1 : 0)) payload.status          = draft.status ? 1 : 0;
    if (draft.type               !== (customer.type               ?? null)) payload.type             = draft.type;

    const origAddresses = parseAddresses(customer.customer_addresses_new ?? customer.addresses)
      .map((a, i) => a.id != null ? a : { ...a, id: `existing-${i}` });
    if (JSON.stringify(addresses) !== JSON.stringify(origAddresses)) {
      payload.customer_addresses_new = addresses;
    }

    if (Object.keys(payload).length === 1) {
      message.info("No changes to save");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/customers/update",
        payload,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data.success) throw new Error(res.data.message);
      notification.success({ message: "Customer updated", placement: "topRight", duration: 2 });
      onSaved({ ...(res.data.data || {}), type: draft.type });
      onClose();
    } catch (e) {
      message.error(e.message || "Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Drawer
      placement="left"
      width={440}
      open={open}
      onClose={onClose}
      destroyOnClose={false}
      mask={false}
      maskClosable={false}
      push={false}
      title={
        <div>
          <div style={{ fontWeight: 700 }}>Edit Customer</div>
          <div style={{ fontSize: 12, color: "#8c8c8c", fontWeight: 400 }}>
            {customer?.customer_name || customer?.customer_mobile || ""}
          </div>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
        </div>
      }
    >
      {/* ── Profile fields ── */}
      <div style={{ display: "grid", gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Customer Name</div>
          <Input
            value={draft.customer_name || ""}
            onChange={e => setField("customer_name", e.target.value)}
            placeholder="Display name"
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Real Name</div>
          <Input
            value={draft.customer_real_name || ""}
            onChange={e => setField("customer_real_name", e.target.value)}
            placeholder="Legal / real name"
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Email</div>
          <Input
            value={draft.customer_email || ""}
            onChange={e => {
              const v = e.target.value;
              setField("customer_email", v);
              if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) setEmailError("Invalid email address");
              else setEmailError("");
            }}
            placeholder="Email address"
            status={emailError ? "error" : ""}
          />
          {emailError && <div style={{ fontSize: 11, color: "#ff4d4f", marginTop: 2 }}>{emailError}</div>}
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Phone</div>
          <div style={{ fontSize: 13, color: "#262626", padding: "4px 0", display: "flex", alignItems: "center", gap: 8 }}>
            <span>{customer?.customer_mobile || "—"}</span>
            {(customer?.country || customer?.customer_country) && (
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                {customer?.country || customer?.customer_country}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Source</div>
            <Select
              style={{ width: "100%" }}
              value={draft.source ?? null}
              onChange={val => setField("source", val ?? null)}
              allowClear
              placeholder="Select source"
              options={sourceOptions.map(o => ({ value: o.value, label: o.label }))}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Type</div>
            <Select
              style={{ width: 90 }}
              value={draft.type ?? null}
              onChange={val => setField("type", val ?? null)}
              allowClear
              placeholder="Type?"
              options={[{ value: 0, label: "B2B" }, { value: 1, label: "B2C" }]}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Active</div>
            <Switch
              checked={!!draft.status}
              onChange={val => setField("status", val)}
              checkedChildren="Active"
              unCheckedChildren="Off"
            />
          </div>
        </div>
      </div>

      {/* ── Accordions: Vehicles + Addresses ── */}
      <Collapse
        accordion
        style={{ marginTop: 20 }}
        activeKey={activeKeys[0] ?? null}
        onChange={key => setActiveKeys(key ? [key] : [])}
        items={[
          // ── Vehicles (B2C only; unknown = prompt) ──────────────────────────
          draft.type !== 0 && {
            key: "vehicles",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <CarOutlined />
                Vehicles
                {draft.type === 1 && vehicles.length > 0 && (
                  <Badge count={vehicles.length} size="small" color="#8c8c8c" />
                )}
                {draft.type === null && (
                  <Tag color="orange" style={{ fontSize: 10, margin: 0, padding: "0 5px" }}>
                    Set type first
                  </Tag>
                )}
              </span>
            ),
            extra: draft.type === 1 && (
              <Button size="small" icon={<PlusOutlined />}
                onClick={e => { e.stopPropagation(); openAddVehicleForm(); }}>
                Add
              </Button>
            ),
            children: draft.type === null ? (
              <div style={{ textAlign: "center", padding: "16px 0", color: "#8c8c8c" }}>
                <QuestionCircleOutlined style={{ fontSize: 22, color: "#d48806", marginBottom: 8, display: "block" }} />
                <div style={{ fontSize: 13, marginBottom: 4 }}>Customer type not set</div>
                <div style={{ fontSize: 12 }}>Set the customer type to <strong>B2C</strong> above to manage vehicles.</div>
              </div>
            ) : (
              <>
                {/* ── Vehicle sub-form ── */}
                {vehicleSubForm !== null && (
                  <div ref={vehicleSubFormRef}
                    style={{ border: `1px solid ${editingVehicleId ? "#d46b08" : "#1677ff"}`, borderRadius: 6, padding: 12, background: editingVehicleId ? "#fff7e6" : "#f0f5ff", marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: editingVehicleId ? "#d46b08" : "#1677ff", marginBottom: 8 }}>
                      {editingVehicleId ? "Edit Vehicle" : "Add New Vehicle"}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px" }}>
                      {/* Brand */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Brand</div>
                        <Select
                          style={{ width: "100%" }}
                          showSearch
                          loading={loadingBrands}
                          placeholder="Select brand"
                          filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                          options={brandOptions}
                          value={vehicleSubForm.brand_id ?? null}
                          onChange={val => {
                            setVehicleSubForm(f => ({ ...f, brand_id: val ?? null, model_id: null }));
                            loadVehicleModels(val);
                          }}
                          allowClear
                        />
                      </div>
                      {/* Model */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Model</div>
                        <Select
                          style={{ width: "100%" }}
                          showSearch
                          loading={loadingModels}
                          placeholder={vehicleSubForm.brand_id ? "Select model" : "Select brand first"}
                          disabled={!vehicleSubForm.brand_id}
                          filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                          options={modelOptions}
                          value={vehicleSubForm.model_id ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, model_id: val ?? null }))}
                          allowClear
                        />
                      </div>
                      {/* Variant */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Variant</div>
                        <Input
                          placeholder="e.g. Sport, LX, SE"
                          value={vehicleSubForm.variant}
                          onChange={e => setVehicleSubForm(f => ({ ...f, variant: e.target.value }))}
                        />
                      </div>
                      {/* Year */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Year</div>
                        <DatePicker
                          style={{ width: "100%" }}
                          picker="year"
                          placeholder="Select year"
                          value={vehicleSubForm.year ? dayjs(vehicleSubForm.year, "YYYY") : null}
                          onChange={(_, str) => setVehicleSubForm(f => ({ ...f, year: str || null }))}
                          disabledDate={d => d && (d.year() < 2000 || d.year() > dayjs().year())}
                        />
                      </div>
                      {/* VIN / Chassis Number */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>VIN / Chassis No.</div>
                        <Input
                          placeholder="e.g. 1HGCM82633A004352"
                          value={vehicleSubForm.vin}
                          onChange={e => setVehicleSubForm(f => ({ ...f, vin: e.target.value }))}
                        />
                      </div>
                      {/* Engine Code */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Engine Code</div>
                        <Input
                          placeholder="e.g. 2AR-FE"
                          value={vehicleSubForm.engine_code}
                          onChange={e => setVehicleSubForm(f => ({ ...f, engine_code: e.target.value }))}
                        />
                      </div>
                      {/* Engine Displacement */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Engine Displacement (L)</div>
                        <Input
                          placeholder="e.g. 2.0"
                          value={vehicleSubForm.engine_displacement}
                          onChange={e => {
                            const val = e.target.value;
                            if (val === "" || /^\d{0,2}(\.\d{0,1})?$/.test(val)) {
                              setVehicleSubForm(f => ({ ...f, engine_displacement: val }));
                            }
                          }}
                        />
                      </div>
                      {/* Engine Layout */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Engine Layout</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select layout"
                          allowClear
                          value={vehicleSubForm.engine_layout ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, engine_layout: val ?? null }))}
                          options={[
                            { value: "inline-3",    label: "Inline-3" },
                            { value: "inline-4",    label: "Inline-4" },
                            { value: "inline-6",    label: "Inline-6" },
                            { value: "v6",          label: "V6" },
                            { value: "v8",          label: "V8" },
                            { value: "v10",         label: "V10" },
                            { value: "v12",         label: "V12" },
                            { value: "flat-4",      label: "Flat-4" },
                            { value: "flat-6",      label: "Flat-6" },
                            { value: "w12",         label: "W12" },
                            { value: "w16",         label: "W16" },
                            { value: "rotary",      label: "Rotary" },
                            { value: "electric",    label: "Electric Motor" },
                          ]}
                        />
                      </div>
                      {/* Transmission Type */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Transmission Type</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select type"
                          allowClear
                          value={vehicleSubForm.transmission_type ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, transmission_type: val ?? null }))}
                          options={[
                            { value: "automatic",      label: "Automatic" },
                            { value: "manual",         label: "Manual" },
                            { value: "cvt",            label: "CVT" },
                            { value: "dct",            label: "DCT (Dual Clutch)" },
                            { value: "amt",            label: "AMT" },
                            { value: "semi-automatic", label: "Semi-Automatic" },
                          ]}
                        />
                      </div>
                      {/* Transmission Code */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Transmission Code</div>
                        <Input
                          placeholder="e.g. A750E"
                          value={vehicleSubForm.transmission_code}
                          onChange={e => setVehicleSubForm(f => ({ ...f, transmission_code: e.target.value }))}
                        />
                      </div>
                      {/* Drivetrain */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Drivetrain</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select drivetrain"
                          allowClear
                          value={vehicleSubForm.drivetrain ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, drivetrain: val ?? null }))}
                          options={[
                            { value: "fwd", label: "FWD" },
                            { value: "rwd", label: "RWD" },
                            { value: "awd", label: "AWD" },
                            { value: "4wd", label: "4WD" },
                          ]}
                        />
                      </div>
                      {/* Fuel Type */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Fuel Type</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select fuel type"
                          allowClear
                          value={vehicleSubForm.fuel_type ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, fuel_type: val ?? null }))}
                          options={[
                            { value: "petrol",       label: "Petrol" },
                            { value: "diesel",       label: "Diesel" },
                            { value: "hybrid",       label: "Hybrid" },
                            { value: "phev",         label: "Plug-in Hybrid" },
                            { value: "electric",     label: "Electric" },
                            { value: "lpg",          label: "LPG" },
                            { value: "cng",          label: "CNG" },
                            { value: "hydrogen",     label: "Hydrogen" },
                          ]}
                        />
                      </div>
                      {/* Country Spec */}
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Country Spec</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select spec"
                          allowClear
                          value={vehicleSubForm.country_spec ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, country_spec: val ?? null }))}
                          options={[
                            { value: "gcc",       label: "GCC" },
                            { value: "us",        label: "US" },
                            { value: "uk",        label: "UK" },
                            { value: "eu",        label: "EU" },
                            { value: "japan",     label: "Japan" },
                            { value: "canada",    label: "Canada" },
                            { value: "australia", label: "Australia" },
                            { value: "china",     label: "China" },
                            { value: "other",     label: "Other" },
                          ]}
                        />
                      </div>
                      {/* Trim Info */}
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Trim Info</div>
                        <Input
                          placeholder="e.g. Sport Edition, Night Package"
                          value={vehicleSubForm.trim_info}
                          onChange={e => setVehicleSubForm(f => ({ ...f, trim_info: e.target.value }))}
                        />
                      </div>
                      {/* Additional Notes */}
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Additional Notes</div>
                        <Input.TextArea
                          rows={3}
                          placeholder="Any extra details about the vehicle..."
                          value={vehicleSubForm.notes}
                          onChange={e => setVehicleSubForm(f => ({ ...f, notes: e.target.value }))}
                        />
                      </div>
                      {/* Is Primary + Status */}
                      <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Checkbox
                          checked={vehicleSubForm.is_primary}
                          onChange={e => setVehicleSubForm(f => ({ ...f, is_primary: e.target.checked }))}
                        >
                          <span style={{ fontSize: 12 }}>Primary vehicle</span>
                        </Checkbox>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12, color: "#8c8c8c" }}>Active</span>
                          <Switch
                            size="small"
                            checked={vehicleSubForm.status}
                            onChange={val => setVehicleSubForm(f => ({ ...f, status: val }))}
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                      <Button size="small" onClick={() => { setVehicleSubForm(null); setEditingVehicleId(null); setModelOptions([]); }}>Cancel</Button>
                      <Button size="small" type="primary" loading={vehiclesSaving}
                        style={editingVehicleId ? { background: "#d46b08", borderColor: "#d46b08" } : {}}
                        onClick={async () => {
                          if (!vehicleSubForm.brand_id) { message.warning("Please select a brand"); return; }
                          if (!vehicleSubForm.model_id) { message.warning("Please select a model"); return; }
                          setVehiclesSaving(true);
                          const isEdit = !!editingVehicleId;
                          try {
                            const vf = vehicleSubForm;
                            const payload = isEdit
                              ? { id: editingVehicleId }
                              : { customer_id: customer.customer_id, brand: vf.brand_id, model: vf.model_id, created_by: authState.id };
                            if (!isEdit || true) {
                              payload.brand               = vf.brand_id;
                              payload.model               = vf.model_id;
                              payload.is_primary_vehicle  = vf.is_primary ? 1 : 0;
                              payload.status              = vf.status    ? 1 : 0;
                              payload.variant             = vf.variant             || null;
                              payload.year                = vf.year                || null;
                              payload.vin                 = vf.vin                 || null;
                              payload.engine_code         = vf.engine_code         || null;
                              payload.engine_displacement = vf.engine_displacement ? parseFloat(vf.engine_displacement) : null;
                              payload.engine_layout       = vf.engine_layout       || null;
                              payload.transmission_type   = vf.transmission_type   || null;
                              payload.transmission_code   = vf.transmission_code   || null;
                              payload.drivetrain          = vf.drivetrain          || null;
                              payload.fuel_type           = vf.fuel_type           || null;
                              payload.country_spec        = vf.country_spec        || null;
                              payload.trim_info           = vf.trim_info           || null;
                              payload.notes               = vf.notes               || null;
                            }
                            const endpoint = isEdit ? "/customer_vehicles/update" : "/customer_vehicles/create";
                            const res = await axios.post(
                              API_DOMAIN + endpoint,
                              payload,
                              { headers: { accessToken: localStorage.getItem("accessToken") } }
                            );
                            if (!res.data.success) throw new Error(res.data.message);
                            message.success(isEdit ? "Vehicle updated" : "Vehicle added");
                            setVehicleSubForm(null);
                            setEditingVehicleId(null);
                            setModelOptions([]);
                            await loadVehicles(customer.customer_id);
                          } catch (e) {
                            message.error(e.message || (isEdit ? "Failed to update vehicle" : "Failed to add vehicle"));
                          } finally {
                            setVehiclesSaving(false);
                          }
                        }}>
                        {editingVehicleId ? "Save Changes" : "Add Vehicle"}
                      </Button>
                    </div>
                  </div>
                )}
                {/* ── Vehicle list ── */}
                {vehiclesLoading
                  ? <div style={{ textAlign: "center", padding: "16px 0" }}><Spin size="small" /></div>
                  : vehicles.length === 0
                    ? <Empty description="No vehicles yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    : vehicles.map(v => {
                      const brandLabel = v.brand_name || v.brand_label || (brandOptions.find(b => b.value === v.brand)?.label) || v.brand || "—";
                      const modelLabel = v.model_name || v.model_label || v.model || "—";
                      const isPrimary  = v.is_primary_vehicle === 1 || v.is_primary === true;
                      const isActive   = v.status !== 0 && v.status !== false;
                      return (
                        <div key={v.id}
                          style={{ border: "1px solid #d9d9d9", borderRadius: 6, padding: "8px 10px",
                                   marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {brandLabel} {modelLabel} {v.year ? `(${v.year})` : ""}
                              {isPrimary  && <Tag color="blue"    style={{ marginLeft: 6, fontSize: 10 }}>Primary</Tag>}
                              {!isActive  && <Tag color="default" style={{ marginLeft: 4, fontSize: 10 }}>Inactive</Tag>}
                            </div>
                            {v.variant && <div style={{ fontSize: 11, color: "#8c8c8c" }}>Variant: {v.variant}</div>}
                            {v.vin && <div style={{ fontSize: 11, color: "#8c8c8c" }}>VIN: {v.vin}</div>}
                            {v.engine_displacement && <div style={{ fontSize: 11, color: "#8c8c8c" }}>{v.engine_displacement}L{v.engine_layout ? ` · ${v.engine_layout}` : ""}{v.fuel_type ? ` · ${v.fuel_type}` : ""}</div>}
                          </div>
                          <div style={{ display: "flex", gap: 2 }}>
                            <Tooltip title="Edit vehicle">
                              <Button type="text" size="small" icon={<EditOutlined />}
                                style={{ color: "#1677ff" }}
                                onClick={() => {
                                  setActiveKeys(["vehicles"]);
                                  setEditingVehicleId(v.id);
                                  setVehicleSubForm({
                                    brand_id:            v.brand ?? null,
                                    model_id:            v.model ?? null,
                                    variant:             v.variant             || "",
                                    year:                v.year ? String(v.year).slice(0, 4) : null,
                                    vin:                 v.vin                 || "",
                                    engine_code:         v.engine_code         || "",
                                    engine_displacement: v.engine_displacement != null ? String(v.engine_displacement) : "",
                                    engine_layout:       v.engine_layout       || null,
                                    transmission_type:   v.transmission_type   || null,
                                    transmission_code:   v.transmission_code   || "",
                                    drivetrain:          v.drivetrain          || null,
                                    fuel_type:           v.fuel_type           || null,
                                    country_spec:        v.country_spec        || null,
                                    trim_info:           v.trim_info           || "",
                                    notes:               v.notes               || "",
                                    is_primary:          v.is_primary_vehicle  === 1,
                                    status:              v.status              !== 0,
                                  });
                                  if (v.brand) loadVehicleModels(v.brand);
                                  setTimeout(() => vehicleSubFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                                }}
                              />
                            </Tooltip>
                            <Button type="text" size="small" danger icon={<DeleteOutlined />}
                              onClick={async () => {
                                try {
                                  const res = await axios.post(
                                    API_DOMAIN + "/customer_vehicles/delete",
                                    { id: v.id },
                                    { headers: { accessToken: localStorage.getItem("accessToken") } }
                                  );
                                  if (!res.data.success) throw new Error(res.data.message);
                                  setVehicles(prev => prev.filter(x => x.id !== v.id));
                                } catch (e) {
                                  message.error(e.message || "Failed to delete vehicle");
                                }
                              }} />
                          </div>
                        </div>
                      );
                    })
                }
              </>
            ),
          },

          // ── Addresses ──────────────────────────────────────────────────────
          {
            key: "addresses",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                Addresses
                {addresses.length > 0 && (
                  <Badge count={addresses.length} size="small" color="#8c8c8c" />
                )}
              </span>
            ),
            extra: (
              <Button size="small" icon={<PlusOutlined />}
                onClick={e => { e.stopPropagation(); openAddSubForm(); }}>
                Add
              </Button>
            ),
            children: (
              <>
                {/* ── Inline address sub-form — rendered at top ── */}
                {subForm !== null && (
                  <div ref={subFormRef}
                    style={{ border: "1px solid #1677ff", borderRadius: 6, padding: 12, background: "#f0f5ff",
                             display: "grid", gap: 8, marginBottom: 12 }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Input placeholder="Name *" value={subForm.name || ""}
                        onChange={e => setSubForm(f => ({ ...f, name: e.target.value }))} />
                      <Input placeholder="Number *" value={subForm.number || ""}
                        onChange={e => setSubForm(f => ({ ...f, number: e.target.value.replace(/\D/g, "") }))} />
                    </div>
                    {isMapsLoaded ? (
                      <Autocomplete
                        onLoad={ac => { autocompleteRef.current = ac; }}
                        onPlaceChanged={() => {
                          const place = autocompleteRef.current?.getPlace();
                          if (place?.formatted_address)
                            setSubForm(f => ({ ...f, address: place.formatted_address }));
                        }}
                      >
                        <Input placeholder="Address *" value={subForm.address || ""}
                          onChange={e => setSubForm(f => ({ ...f, address: e.target.value }))} />
                      </Autocomplete>
                    ) : (
                      <Input placeholder="Address *" value={subForm.address || ""}
                        onChange={e => setSubForm(f => ({ ...f, address: e.target.value }))} />
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <Radio.Group value={subForm.type || "Home"}
                        onChange={e => setSubForm(f => ({ ...f, type: e.target.value }))}
                        options={[{ label: "Home", value: "Home" }, { label: "Office", value: "Office" }]}
                        optionType="button" buttonStyle="solid" size="small" />
                      <TimePicker.RangePicker format="hh:mm A" use12Hours allowClear size="small"
                        value={subForm.pref_time?.length === 2
                          ? [dayjs(subForm.pref_time[0]), dayjs(subForm.pref_time[1])]
                          : null}
                        onChange={val => setSubForm(f => ({
                          ...f, pref_time: val ? [val[0].toISOString(), val[1].toISOString()] : null,
                        }))} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                      <Button size="small" onClick={() => setSubForm(null)}>Cancel</Button>
                      <Button size="small" type="primary" onClick={saveSubForm}>Save Address</Button>
                    </div>
                  </div>
                )}

                {/* ── Address list ── */}
                <div style={{ display: "grid", gap: 8 }}>
                  {addresses.length === 0 && (
                    <Empty description="No addresses" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                  {addresses.map(addr => (
                    <div key={addr.id}
                      style={{ border: "1px solid #d9d9d9", borderRadius: 6, padding: "8px 12px", background: "#fafafa" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <span style={{ fontSize: 11, background: "#f0f0f0", color: "#595959", padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>
                          {addr.type}
                        </span>
                        <div style={{ display: "flex", gap: 4 }}>
                          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => editAddress(addr)} />
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => deleteAddress(addr.id)} />
                        </div>
                      </div>
                      <div style={{ fontWeight: 600, marginTop: 4 }}>
                        {addr.name}
                        <span style={{ fontWeight: 400, color: "#8c8c8c", fontSize: 12, marginLeft: 8 }}>{addr.number}</span>
                      </div>
                      <div style={{ fontSize: 12, color: "#595959", marginTop: 2 }}>{addr.address}</div>
                      {addr.pref_time && Array.isArray(addr.pref_time) && addr.pref_time.length === 2 && (
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
                          {dayjs(addr.pref_time[0]).format("hh:mm A")} – {dayjs(addr.pref_time[1]).format("hh:mm A")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ),
          },
        ].filter(Boolean)}
      />
    </Drawer>
  );
}

// ── Chat Panel Header ─────────────────────────────────────────────────────────
function ChatPanelHeader({ chat, pinned, onPinToggle, onClose, isMobile, onChatPatch, isDark, onThemeToggle }) {
  const theme = ct(isDark ?? true);
  const tsCfg = chat?.ticket_status != null ? TICKET_STATUS[chat.ticket_status] : null;

  const [customer,     setCustomer]   = useState(null);
  const [localType,    setLocalType]  = useState(null);
  const [typeLoading,  setTypeLoading] = useState(false);
  const [editOpen,     setEditOpen]   = useState(false);
  const typeInFlight = useRef(false);

  // Load customer profile whenever the chat phone changes
  useEffect(() => {
    if (!chat?.phone) { setCustomer(null); return; }
    setCustomer(null);
    const headers = { accessToken: localStorage.getItem("accessToken") };
    axios.get(API_DOMAIN + "/customers/get_full_profile", { params: { phone: chat.phone }, headers })
      .then(res => {
        if (res.data.success && res.data.data?.length > 0) {
          setCustomer(res.data.data[0]);
        } else {
          axios.post(API_DOMAIN + "/whatsapp/customer_add", { name: chat.customer_name || chat.phone, number: chat.phone }, { headers })
            .then(() => {
              message.success("Saved the customer automatically");
              axios.get(API_DOMAIN + "/customers/get_full_profile", { params: { phone: chat.phone }, headers })
                .then(r => { if (r.data.success && r.data.data?.length > 0) setCustomer(r.data.data[0]); })
                .catch(() => {});
            }).catch(() => {});
        }
      }).catch(() => {});
  }, [chat?.phone]); // eslint-disable-line

  // Sync localType whenever the customer record loads/changes
  useEffect(() => {
    setLocalType(customer?.type ?? null);
  }, [customer]);

  const handleTypeChange = async (val) => {
    if (!customer?.customer_id || typeInFlight.current) return;
    typeInFlight.current = true;
    const prev = customer?.type ?? null;
    setLocalType(val ?? null);
    setTypeLoading(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/customers/update",
        { customer_id: customer.customer_id, type: val ?? null },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data.success) throw new Error(res.data.message);
      setCustomer(c => ({ ...c, type: val ?? null }));
      onChatPatch?.({ customer_type: val ?? null });
    } catch {
      setLocalType(prev);
      message.error("Failed to update customer type");
    } finally {
      setTypeLoading(false);
      typeInFlight.current = false;
    }
  };

  const handleCustomerSaved = (updatedCustomer) => {
    if (!updatedCustomer) return;
    setCustomer(updatedCustomer);
    setLocalType(updatedCustomer?.type ?? null);
    onChatPatch?.({ customer_type: updatedCustomer.type ?? null });
  };

  const statusMenuItems = [
    { key: 1, label: <span style={{ color: TICKET_STATUS[1].color, fontWeight: 600 }}>Open</span> },
    { key: 3, label: <span style={{ color: TICKET_STATUS[3].color, fontWeight: 600 }}>Pending</span> },
    { key: 2, label: <span style={{ color: TICKET_STATUS[2].color, fontWeight: 600 }}>Closed</span> },
  ];

  // Prefer customer.type (live, from DB) over the stale fetch_messages snapshot
  const customerType = customer?.type ?? chat?.customer_type;
  const avatarIcon   = customerType === 0 ? <ShopOutlined /> : customerType === 1 ? <UserOutlined /> : <QuestionCircleOutlined />;
  const avatarColor  = customerType === 0 ? "#1677ff" : customerType === 1 ? "#722ed1" : "#d48806";

  return (
    <>
      <div className="cpt" style={{ display: "flex", alignItems: "stretch", gap: 8, width: "100%" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <Avatar size={32} icon={avatarIcon} style={{ backgroundColor: avatarColor, flexShrink: 0 }} />
          <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: 3, width: 32 }}>
            <Tooltip title="Edit customer profile" placement="right">
              <Button
                disabled={!customer}
                icon={<EditOutlined style={{ fontSize: 12 }} />}
                onClick={() => setEditOpen(true)}
                style={{ width: 32, flex: 1, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", color: theme.textSub, borderColor: theme.border, background: "transparent" }}
              />
            </Tooltip>
            <Tooltip title="Create Query" placement="right">
              <Button
                disabled={!customer}
                icon={<PlusOutlined style={{ fontSize: 12 }} />}
                style={{ width: 32, flex: 1, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,168,132,0.12)", borderColor: "#00a884", color: "#00a884" }}
              />
            </Tooltip>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textAlign: "left" }}>
          <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: theme.text }}>
            {chat?.customer_name || chat?.phone || "Unknown"}
          </div>
          {customer?.customer_real_name && (
            <div style={{ fontSize: 11, color: theme.textSub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
              {customer.customer_real_name}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
            {(() => {
              const info = getCardCountry(chat?.phone);
              return info ? (
                <Tooltip title={info.name}>
                  <img src={`https://cdn.clare.ai/flags/${info.code}.svg`} alt={info.name}
                    style={{ width: 14, height: 14, objectFit: "contain", borderRadius: 2, flexShrink: 0, cursor: "default" }} />
                </Tooltip>
              ) : null;
            })()}
            {chat?.phone && (
              <span style={{ fontSize: 11, color: "#8696a0", flexShrink: 0 }}>
                <CopyablePhone phone={chat.phone} />
              </span>
            )}
            {customer?.create_date && (
              <Tag style={{ fontSize: 10, margin: 0, padding: "0 5px", flexShrink: 0, color: theme.textSub, border: `1px solid ${theme.border}`, background: theme.pill, display: "inline-flex", alignItems: "center", gap: 3 }}>
                <CalendarOutlined style={{ fontSize: 9 }} />
                Since {dayjs(customer.create_date).format("MMM YYYY")}
              </Tag>
            )}
            <Dropdown trigger={["click"]} menu={{ items: statusMenuItems, onClick: ({ key }) => { /* API later */ } }}>
              <Tag style={{ fontSize: 10, margin: 0, padding: "0 5px", cursor: "pointer", fontWeight: 600, flexShrink: 0, userSelect: "none", color: tsCfg ? tsCfg.color : theme.textSub, background: tsCfg ? tsCfg.bg : theme.pill, border: tsCfg ? `1px solid ${tsCfg.border}` : `1px solid ${theme.border}` }} onClick={e => e.stopPropagation()}>
                {tsCfg ? tsCfg.label : "Status"} ▾
              </Tag>
            </Dropdown>
          </div>
          {/* Expiry timer + B2B/B2C select + edit pencil */}
          <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {chat?.expiry?.isValid() && (() => {
              const exp = expiryDisplay(chat.expiry);
              const isExpToday = chat.expiry.isSame(dayjs(), "day");
              const expLabel = isExpToday ? chat.expiry.format("hh:mm a") : `tomorrow ${chat.expiry.format("hh:mm a")}`;
              return (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 6, border: `1px solid ${exp.color}`, background: theme.pill, flexShrink: 0 }}>
                  <ClockCircleOutlined style={{ fontSize: 11, color: exp.color }} />
                  <span style={{ fontSize: 11, color: exp.color, fontWeight: 600 }}>{exp.label} · {expLabel}</span>
                </div>
              );
            })()}
            {/* Type chip — same pattern as status chip */}
            {(() => {
              const typeCfg = localType === 0
                ? { label: "B2B", color: "#1677ff", bg: "#e6f4ff", border: "#91caff" }
                : localType === 1
                ? { label: "B2C", color: "#722ed1", bg: "#f9f0ff", border: "#d3adf7" }
                : null;
              const typeMenuItems = [
                { key: "0", label: <span style={{ color: "#1677ff", fontWeight: 600 }}>B2B</span> },
                { key: "1", label: <span style={{ color: "#722ed1", fontWeight: 600 }}>B2C</span> },
                { type: "divider" },
                { key: "clear", label: <span style={{ color: "#8c8c8c" }}>Clear</span> },
              ];
              return (
                <Dropdown
                  trigger={["click"]}
                  disabled={!customer || typeLoading}
                  menu={{ items: typeMenuItems, onClick: ({ key }) => handleTypeChange(key === "clear" ? null : Number(key)) }}
                >
                  <Tag
                    onClick={e => e.stopPropagation()}
                    style={{
                      fontSize: 10, margin: 0, padding: "0 5px", flexShrink: 0, userSelect: "none",
                      cursor: (!customer || typeLoading) ? "not-allowed" : "pointer", fontWeight: 600,
                      color: typeCfg ? typeCfg.color : theme.textSub,
                      background: typeCfg ? typeCfg.bg : theme.pill,
                      border: typeCfg ? `1px solid ${typeCfg.border}` : `1px solid ${theme.border}`,
                    }}
                  >
                    {typeLoading ? <Spin size="small" /> : (typeCfg ? typeCfg.label : "Type")} ▾
                  </Tag>
                </Dropdown>
              );
            })()}
          </div>
          {chat?.agent && (
            <div style={{ marginTop: 3 }}>
              <Tag style={{ fontSize: 10, margin: 0, maxWidth: "100%", border: `1.5px solid ${getAgentColor(chat.agent)}`, color: getAgentColor(chat.agent), background: "transparent", display: "inline-flex", alignItems: "center", gap: 3, overflow: "hidden" }}>
                <Avatar size={11} style={{ backgroundColor: getAgentColor(chat.agent), fontSize: 7, flexShrink: 0 }}>{agentInitials(chat.agent)}</Avatar>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{chat.agent}</span>
              </Tag>
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0, alignSelf: "flex-start" }}>
          {!pinned && <Tooltip title="Close"><Button type="text" size="small" icon={<CloseCircleOutlined />} onClick={onClose} style={{ color: "#8696a0" }} /></Tooltip>}
          {!isMobile && (
            <Tooltip title={pinned ? "Unpin" : "Pin to sidebar"}>
              <Button type={pinned ? "primary" : "text"} size="small" icon={pinned ? <PushpinFilled style={{ color: pinned ? undefined : "#8696a0" }} /> : <PushpinOutlined style={{ color: "#8696a0" }} />} onClick={onPinToggle} />
            </Tooltip>
          )}
          <Tooltip title={isDark ? "Switch to light mode" : "Switch to dark mode"} placement="left">
            <Switch
              size="small"
              checked={isDark}
              onChange={onThemeToggle}
              checkedChildren="D"
              unCheckedChildren="L"
              style={{ marginTop: 2 }}
            />
          </Tooltip>
        </div>
      </div>
      <CustomerEditDrawer
        customer={customer}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={handleCustomerSaved}
      />
    </>
  );
}

// ── Gallery Thumb ─────────────────────────────────────────────────────────────
function getDocMeta(fileName) {
  const ext = (fileName || "").split(".").pop().toLowerCase();
  const map = {
    pdf:  { icon: FilePdfOutlined,   color: "#ff4d4f" },
    doc:  { icon: FileWordOutlined,  color: "#1677ff" },
    docx: { icon: FileWordOutlined,  color: "#1677ff" },
    xls:  { icon: FileExcelOutlined, color: "#52c41a" },
    xlsx: { icon: FileExcelOutlined, color: "#52c41a" },
    csv:  { icon: FileExcelOutlined, color: "#52c41a" },
    zip:  { icon: FileZipOutlined,   color: "#722ed1" },
    rar:  { icon: FileZipOutlined,   color: "#722ed1" },
    txt:  { icon: FileTextOutlined,  color: "#8696a0" },
    ppt:  { icon: FileOutlined,      color: "#fa8c16" },
    pptx: { icon: FileOutlined,      color: "#fa8c16" },
  };
  const meta = map[ext] || { icon: FileOutlined, color: "#8696a0" };
  return { Icon: meta.icon, color: meta.color, ext: ext.toUpperCase() || "FILE" };
}

function fmtShortDate(d) {
  return d.format(d.isSame(dayjs(), "year") ? "DD MMM" : "DD MMM YYYY");
}
function fmtShortDateTime(d) {
  return d.format(d.isSame(dayjs(), "year") ? "DD MMM · hh:mm A" : "DD MMM YYYY · hh:mm A");
}

function OwnerBadge({ isOut }) {
  return (
    <Tooltip title={isOut ? "Sent" : "Received"}>
      <span style={{ position: "absolute", bottom: 5, left: 5, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "default" }}>
        {isOut
          ? <ArrowUpOutlined   style={{ fontSize: 9, color: "#25d366" }} />
          : <ArrowDownOutlined style={{ fontSize: 9, color: "#8696a0" }} />}
      </span>
    </Tooltip>
  );
}

function GalleryAudioCard({ src, ts, isOut }) {
  const audioRef = useRef(null);
  const [playing,     setPlaying]     = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration,    setDuration]    = useState(0);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else         { a.play();  setPlaying(true);  }
  };

  const R            = 34;
  const CX           = 50;
  const CY           = 50;
  const circumference = 2 * Math.PI * R;
  const pct           = duration ? currentTime / duration : 0;
  const offset        = circumference * (1 - pct);

  return (
    <div style={{ aspectRatio: "1", borderRadius: 4, background: "#202c33", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      <audio ref={audioRef} src={src}
        onTimeUpdate={e  => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={e => setDuration(e.target.duration)}
        onEnded={() => { setPlaying(false); setCurrentTime(0); }}
      />
      <svg width="84" height="84" viewBox="0 0 100 100" style={{ position: "absolute" }}>
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="#25d366" strokeWidth="4"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform={`rotate(-90 ${CX} ${CY})`}
          style={{ transition: "stroke-dashoffset 0.25s linear" }}
        />
      </svg>
      <Button type="text" icon={playing ? <PauseCircleOutlined style={{ fontSize: 26 }} /> : <PlayCircleOutlined style={{ fontSize: 26 }} />}
        onClick={togglePlay}
        style={{ color: "#e9edef", zIndex: 1, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
      />
      {ts && (
        <span style={{ position: "absolute", bottom: 5, right: 6, fontSize: 9, color: "#667781" }}>
          {fmtShortDateTime(ts)}
        </span>
      )}
      <OwnerBadge isOut={isOut} />
    </div>
  );
}

function GalleryThumb({ item, onViewInChat }) {
  const type    = (item.type || "").toLowerCase();
  const rawUrl  = item.data || null;
  const scrollCtx = useContext(MediaScrollCtx);
  const cachedEntry = rawUrl ? _mediaCache.get(rawUrl) : null;
  const initialUrl  = cachedEntry && cachedEntry.expiresAt > Date.now() + 60_000 ? cachedEntry.signedUrl : null;
  const [signedUrl, setSignedUrl]   = useState(initialUrl);
  const [loading, setLoading]       = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [videoOpen, setVideoOpen]   = useState(false);
  const ref = useRef(null);
  const ts  = item.timestamp ? dayjs(parseInt(item.timestamp) * 1000) : null;

  useEffect(() => {
    if (!rawUrl || signedUrl) return;
    const el = ref.current; const root = scrollCtx?.current || null;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      setLoading(true);
      _resolveMediaUrl(rawUrl).then(url => { setSignedUrl(url); setLoading(false); });
    }, { root, threshold: 0, rootMargin: "200px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [rawUrl, signedUrl]); // eslint-disable-line

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!rawUrl || downloading) return;
    setDownloading(true);
    axios.post(
      API_DOMAIN + "/wati_messages/download_wati_media_file",
      { file_name: rawUrl },
      { headers: { accessToken: localStorage.getItem("accessToken") }, responseType: "blob" }
    ).then(res => {
      const cd = res.headers["content-disposition"] || "";
      const nameMatch = cd.match(/filename="?([^";\n]+)"?/i);
      const fileName  = nameMatch?.[1] || item.fileName || rawUrl.split("/").pop() || "download";
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = fileName;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(blobUrl);
    }).catch(() => message.error("Download failed."))
      .finally(() => setDownloading(false));
  };

  const eyeBtn = (
    <button onClick={e => { e.stopPropagation(); onViewInChat?.(item.id); }}
      style={{ position: "absolute", top: 5, right: 5, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.55)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
      <EyeOutlined style={{ fontSize: 11 }} />
    </button>
  );

  if (type === "audio") return (
    <div ref={ref} style={{ aspectRatio: "1", borderRadius: 4, overflow: "hidden", position: "relative" }}>
      {loading ? (
        <div style={{ width: "100%", height: "100%", background: "#202c33", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spin size="small" />
        </div>
      ) : signedUrl ? (
        <GalleryAudioCard src={signedUrl} ts={ts} isOut={item.owner === true} />
      ) : (
        <div style={{ width: "100%", height: "100%", background: "#202c33", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <AudioOutlined style={{ fontSize: 28, color: "#8696a0" }} />
          <span style={{ fontSize: 10, color: "#8696a0" }}>Voice message</span>
        </div>
      )}
      {eyeBtn}
    </div>
  );

  if (type === "document") {
    const { Icon, color, ext } = getDocMeta(item.fileName);
    return (
      <div ref={ref} style={{ aspectRatio: "1", borderRadius: 4, background: "#202c33", position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, overflow: "hidden", padding: 8 }}>
        <Icon style={{ fontSize: 36, color }} />
        <span style={{ fontSize: 10, fontWeight: 700, color, letterSpacing: "0.5px" }}>{ext}</span>
        <span style={{ fontSize: 10, color: "#8696a0", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "90%" }}>
          {item.fileName || "Document"}
        </span>
        {ts && <span style={{ fontSize: 9, color: "#667781" }}>{fmtShortDateTime(ts)}</span>}
        <OwnerBadge isOut={item.owner === true} />
        {eyeBtn}
        <button onClick={handleDownload} disabled={downloading}
          style={{ position: "absolute", bottom: 6, right: 6, width: 26, height: 26, borderRadius: "50%", background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", cursor: downloading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {downloading ? <Spin size="small" /> : <DownloadOutlined style={{ fontSize: 13 }} />}
        </button>
      </div>
    );
  }

  // image / video — square grid tile
  return (
    <div ref={ref} onClick={() => signedUrl && type === "video" && setVideoOpen(true)}
      style={{ aspectRatio: "1", overflow: "hidden", borderRadius: 4, background: "#202c33", cursor: signedUrl ? "pointer" : "default", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {loading && <Spin size="small" />}
      {!loading && !signedUrl && (type === "image"
        ? <PictureOutlined style={{ fontSize: 22, color: "#8696a0" }} />
        : <PlayCircleOutlined style={{ fontSize: 22, color: "#8696a0" }} />)}
      {!loading && signedUrl && type === "image" && (
        <Image src={signedUrl} style={{ width: "100%", height: "100%", objectFit: "cover" }}
          wrapperStyle={{ width: "100%", height: "100%" }} preview={{ mask: false }} />
      )}
      {!loading && signedUrl && type === "video" && (
        <video src={signedUrl} preload="metadata"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" }} />
      )}
      {type === "video" && signedUrl && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.25)" }}>
          <PlayCircleOutlined style={{ fontSize: 26, color: "#fff" }} />
        </div>
      )}
      {ts && (
        <span style={{ position: "absolute", bottom: 3, right: 5, fontSize: 9, color: "rgba(255,255,255,0.75)", textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>
          {fmtShortDateTime(ts)}
        </span>
      )}
      <OwnerBadge isOut={item.owner === true} />
      {eyeBtn}
      {type === "video" && (
        <Modal open={videoOpen} onCancel={() => setVideoOpen(false)} footer={null} centered destroyOnClose
          width={Math.min(window.innerWidth * 0.88, 900)}
          styles={{ body: { padding: 0, background: "#000", borderRadius: 8, overflow: "hidden" }, mask: { backdropFilter: "blur(4px)" } }}
          closable={false}>
          {videoOpen && <video src={signedUrl} controls autoPlay style={{ width: "100%", maxHeight: "75vh", display: "block", outline: "none" }} />}
        </Modal>
      )}
    </div>
  );
}

// ── Chat Panel Messages ───────────────────────────────────────────────────────
function ChatPanelMessages({ chat }) {
  const isDark = React.useContext(ChatThemeCtx);
  const theme  = ct(isDark);
  const { authState } = useContext(AuthContext);
  const { watiChatUpdates } = useWatiSocket();
  const [messageText, setMessageText]   = useState("");
  const [messages, setMessages]         = useState([]);
  const [loadingMsgs, setLoadingMsgs]   = useState(false);
  const [hasMore, setHasMore]           = useState(true);
  const [activeTab, setActiveTab]         = useState("chat");
  const [galleryTypeFilter, setGalleryTypeFilter]   = useState(null);
  const [galleryOwnerFilter, setGalleryOwnerFilter] = useState(null);
  const [customerQueries, setCustomerQueries]       = useState([]);
  const [queriesLoading, setQueriesLoading]         = useState(false);
  const [scrollToMsgId, setScrollToMsgId]           = useState(null);
  const [highlightedMsgId, setHighlightedMsgId]     = useState(null);
  const [isRecording, setIsRecording]       = useState(false);
  const [recordingSecs, setRecordingSecs]   = useState(0);
  const [pendingAudio, setPendingAudio]     = useState(null); // { blob, url }
  const [isSendingAudio, setIsSendingAudio] = useState(false);
  const [quotedMessage, setQuotedMessage]   = useState(null); // { id, text, type, owner }
  const [pendingLink, setPendingLink]       = useState(null);
  const [inputLaunching, setInputLaunching] = useState(false);
  const [pendingFiles, setPendingFiles]     = useState([]);   // [{ id, file, previewUrl, caption }]
  const [isSendingFiles, setIsSendingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // tempId → 0-100
  const [isDragOver, setIsDragOver]         = useState(false);
  const fileInputRef         = useRef(null);
  const mediaRecorderRef    = useRef(null);
  const audioChunksRef      = useRef([]);
  const recordingTimerRef   = useRef(null);
  const recordingSecsRef    = useRef(0);
  const analyserRef         = useRef(null);
  const analyserCtxRef      = useRef(null);
  const waveAnimRef         = useRef(null);
  const waveCanvasRef       = useRef(null);
  const waveDataRef         = useRef([]);
  const pendingTempMap      = useRef(new Map()); // localMessageId → tempId for socket dedup
  const watiMsgProcessedRef = useRef(0);
  const chatIdRef           = useRef(null);
  const scrollRef      = useRef(null);
  const galleryScrollRef = useRef(null);
  const messagesEnd    = useRef(null);
  const restoreScroll  = useRef(null);
  const seekMsgIdRef   = useRef(null);
  const seekAttemptsRef = useRef(0);
  const hasMoreRef     = useRef(true);

  const fetchMessages = async (chatId, lastMessageId = null) => {
    setLoadingMsgs(true);
    try {
      const res = await axios.post(
        CHAT_MESSAGES_API,
        { chatId, lastMessageId },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (res.data.success) {
        const raw = Array.isArray(res.data.data) ? res.data.data : (res.data.data?.data || []);
        const items = [...raw].reverse();
        if (lastMessageId) {
          if (raw.length === 0) {
            notification.info({ message: "No older messages", placement: "topRight", duration: 2 });
            setHasMore(false);
          } else {
            if (scrollRef.current) {
              restoreScroll.current = { scrollTop: scrollRef.current.scrollTop, scrollHeight: scrollRef.current.scrollHeight };
            }
            setMessages(prev => [...items, ...prev]);
            setHasMore(true);
          }
        } else {
          setMessages(items);
          setHasMore(raw.length > 0);
          markChatAsRead();
        }
      }
    } catch {
      message.error("Failed to load messages.");
    } finally {
      setLoadingMsgs(false);
    }
  };

  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  useEffect(() => {
    seekMsgIdRef.current = null;
    seekAttemptsRef.current = 0;
    notification.destroy("seeking-msg");
    if (!chat?.conversationId) { setMessages([]); setHasMore(true); setLoadingMsgs(false); return; }
    setMessages([]); setHasMore(true); setLoadingMsgs(false);
    restoreScroll.current = null;
    fetchMessages(chat.conversationId);
  }, [chat?.id]); // eslint-disable-line

  useEffect(() => {
    if (restoreScroll.current && scrollRef.current) {
      const { scrollTop, scrollHeight } = restoreScroll.current;
      scrollRef.current.scrollTop = scrollTop + (scrollRef.current.scrollHeight - scrollHeight);
      restoreScroll.current = null;
    }

    if (seekMsgIdRef.current) {
      const targetId = seekMsgIdRef.current;
      const oldestId = messages[0]?.id || null;
      setTimeout(() => {
        const el = document.getElementById(`msg-${targetId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          setHighlightedMsgId(targetId);
          setTimeout(() => setHighlightedMsgId(null), 2000);
          notification.destroy("seeking-msg");
          seekMsgIdRef.current = null;
          seekAttemptsRef.current = 0;
        } else if (seekAttemptsRef.current >= 2 || !hasMoreRef.current) {
          notification.destroy("seeking-msg");
          notification.warning({ message: "Message not found", description: "The quoted message could not be loaded.", placement: "topRight", duration: 3 });
          seekMsgIdRef.current = null;
          seekAttemptsRef.current = 0;
        } else {
          seekAttemptsRef.current += 1;
          fetchMessages(chat.conversationId, oldestId);
        }
      }, 150);
      return;
    }

    if (messages.length > 0) {
      messagesEnd.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [messages]); // eslint-disable-line

  const handleScroll = (e) => {
    if (e.target.scrollTop < 60 && hasMore && !loadingMsgs && messages.length > 0) {
      fetchMessages(chat.conversationId, messages[0]?.id || null);
    }
  };

  // Keep chatIdRef current so the socket effect can compare without stale closure
  useEffect(() => { chatIdRef.current = chat?.id ?? null; }, [chat?.id]);

  const markChatAsRead = () => {
    if (localStorage.getItem("wati_read_receipts") === "false") return;
    const chatId = chat?.id;
    if (!chatId) return;
    axios.post(MARK_AS_READ_API, { chatId }, { headers: { accessToken: localStorage.getItem("accessToken") } }).catch(() => {});
  };

  // Socket effect: live message updates for the currently open chat
  useEffect(() => {
    if (watiChatUpdates.length <= watiMsgProcessedRef.current) return;
    const newUpdates = watiChatUpdates.slice(watiMsgProcessedRef.current);
    watiMsgProcessedRef.current = watiChatUpdates.length;

    newUpdates.forEach(update => {

      // ── New message (incoming from customer or outgoing from any operator) ──
      if (update.target === "sendTextMessage") {
        const channelId = update["0"]?.message?.conViewModel?.channelId;
        if (channelId !== null) return; // null-channel only (QuerysetV4's channel)
        const convId = update["0"]?.conversationId;
        if (convId !== chatIdRef.current) return;

        const mType  = update["0"].messageType || "text";
        const isMedia = ["image", "video", "audio", "sticker"].includes(mType);
        const isDoc   = mType === "document";
        const isAudio = mType === "audio";
        const newMsg = {
          id:                 update["0"].messageId,
          eventType:          "message",
          type:               mType,
          text:               isAudio ? null : update["0"].message.text,
          owner:              update["0"].message.isOwner,
          statusString:       update["0"].message.statusString,
          timestamp:          String(update["0"].timestamp),
          operatorName:       update["0"].message.operatorName,
          replySourceMessage: update["0"].replySourceMessage || null,
          _animate:           true,
          ...((isMedia || isDoc) && { data: isAudio ? (update["0"].message.text || null) : (update["0"].message.data || null) }),
        };

        setMessages(prev => {
          const localMsgId = update["0"].localMessageId;
          const tempId = localMsgId ? pendingTempMap.current.get(localMsgId) : undefined;
          if (tempId) {
            pendingTempMap.current.delete(localMsgId);
            const idx = prev.findIndex(m => m.id === tempId);
            if (idx !== -1) {
              const next = [...prev];
              next[idx] = { ...newMsg, _animate: false }; // replacing existing bubble — no re-entrance
              return next;
            }
          }
          if (prev.some(m => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });

        if (update["0"].message.isOwner === false) {
          markChatAsRead();
        }

        setTimeout(() => {
          const el = scrollRef.current;
          if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
            messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
          }
        }, 50);
      }

      // ── Delivery / read-receipt status update on a rendered message ─────────
      if (update.target === "updatingOperatorMessageStatus") {
        const msgData = Array.isArray(update["0"]) ? update["0"][0] : update["0"];
        if (!msgData || msgData.conversationId !== chatIdRef.current) return;

        const localMsgId = msgData.localMessageId;
        const tempId = localMsgId ? pendingTempMap.current.get(localMsgId) : undefined;

        if (tempId) {
          // Replace optimistic bubble with confirmed server message
          pendingTempMap.current.delete(localMsgId);
          const isMediaMsg = ["image", "video", "audio", "document"].includes(msgData.type);
          setMessages(prev => {
            const idx = prev.findIndex(m => m.id === tempId);
            if (idx === -1) return prev;
            const next = [...prev];
            next[idx] = {
              ...next[idx],
              id:               msgData.id,
              statusString:     msgData.statusString,
              _animate:         false,
              _optimistic:      false,
              _localPreviewUrl: null,
              ...(isMediaMsg && {
                text: msgData.media?.caption || null,  // caption to display
                data: msgData.text || null,            // file path → signed URL
              }),
            };
            return next;
          });
        } else {
          // Status tick on an already-rendered message — also correct text/data for media types
          const isMediaMsg = ["image", "video", "audio", "document"].includes(msgData.type);
          setMessages(prev => prev.map(m => {
            if (m.id !== msgData.id) return m;
            return {
              ...m,
              statusString: msgData.statusString,
              ...(isMediaMsg && {
                text: msgData.media?.caption || null,
                data: msgData.text || null,
              }),
            };
          }));
        }
      }

      // ── Operator message sent from another browser session ───────────────────
      if (update.target === "messageFromOperator") {
        const msg = update["0"];
        if (!msg || msg.conversationId !== chatIdRef.current) return;

        const isMedia = ["image", "video", "audio", "sticker"].includes(msg.type);
        const isDoc   = msg.type === "document";
        const isAudio = msg.type === "audio";
        const newMsg = {
          id:                 msg.id,
          localMessageId:     msg.localMessageId,
          eventType:          "message",
          type:               msg.type || "text",
          text:               (isAudio || isDoc) ? (msg.media?.caption || null) : msg.text,
          owner:              true,
          statusString:       msg.statusString,
          timestamp:          String(msg.time || Math.floor(Date.now() / 1000)),
          operatorName:       msg.operatorName,
          replySourceMessage: msg.replyContextId ? { id: msg.replyContextId } : null,
          _animate:           true,
          ...((isMedia || isDoc) && { data: (isAudio || isDoc) ? (msg.text || null) : (msg.media || null) }),
        };

        setMessages(prev => {
          const localMsgId = msg.localMessageId;
          const tempId = localMsgId ? pendingTempMap.current.get(localMsgId) : undefined;
          if (tempId) {
            pendingTempMap.current.delete(localMsgId);
            const idx = prev.findIndex(m => m.id === tempId);
            if (idx !== -1) {
              const next = [...prev];
              next[idx] = { ...newMsg, _animate: false };
              return next;
            }
          }
          if (prev.some(m => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });

        setTimeout(() => {
          const el = scrollRef.current;
          if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
            messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
          }
        }, 50);
      }
    });
  }, [watiChatUpdates]);

  const handleSend = async () => {
    if (!messageText.trim()) return;
    const text = messageText.trim();
    const localMessageId = uuidv4();
    const tempId = `temp_${Date.now()}`;
    const replyContextId = quotedMessage?.id || null;

    setInputLaunching(true);
    setTimeout(() => setInputLaunching(false), 300);

    const optimisticMsg = {
      id: tempId,
      localMessageId,
      eventType: "message",
      type: "text",
      owner: true,
      text,
      statusString: "SENDING",
      timestamp: String(Math.floor(Date.now() / 1000)),
      _optimistic: true,
      _animate: true,
      replyTo: quotedMessage
        ? { id: quotedMessage.id, text: quotedMessage.text, type: quotedMessage.type, owner: quotedMessage.owner }
        : null,
    };

    pendingTempMap.current.set(localMessageId, tempId);
    setMessages(prev => [...prev, optimisticMsg]);
    setMessageText("");
    setQuotedMessage(null);

    try {
      await axios.post(
        SEND_TEXT_API,
        { whatsappId: chat.phone, text, localMessageId, userId: authState.id, replyContextId },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, statusString: "SENT" } : m
      ));
    } catch {
      pendingTempMap.current.delete(localMessageId);
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, statusString: "FAILED" } : m
      ));
      message.error("Failed to send message.");
    }
  };

  const retrySend = async (failedMsg) => {
    const { id: tempId, localMessageId, text, replyTo } = failedMsg;
    pendingTempMap.current.set(localMessageId, tempId);
    setMessages(prev => prev.map(m =>
      m.id === tempId ? { ...m, statusString: "SENDING" } : m
    ));
    try {
      await axios.post(
        SEND_TEXT_API,
        { whatsappId: chat.phone, text, localMessageId, userId: authState.id, replyContextId: replyTo?.id || null },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, statusString: "SENT" } : m
      ));
    } catch {
      pendingTempMap.current.delete(localMessageId);
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, statusString: "FAILED" } : m
      ));
      message.error("Failed to send message.");
    }
  };

  const isFirefox = navigator.userAgent.includes("Firefox");

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          autoGainControl: true,
          noiseSuppression: false,
          echoCancellation: false,
        },
      });
      audioChunksRef.current = [];
      recordingSecsRef.current = 0;

      // Firefox: route through Web Audio gain node to bypass its broken
      // MediaRecorder audio pipeline which silences normal voice frequencies.
      let recordStream = stream;
      let audioCtx = null;
      if (isFirefox) {
        audioCtx = new AudioContext();
        const source = audioCtx.createMediaStreamSource(stream);
        const gain = audioCtx.createGain();
        gain.gain.value = 3.0;
        const dest = audioCtx.createMediaStreamDestination();
        source.connect(gain);
        gain.connect(dest);
        recordStream = dest.stream;
      }

      const mimeType = (isFirefox
        ? ["audio/ogg;codecs=opus", "audio/webm;codecs=opus"]
        : ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"]
      ).find(t => MediaRecorder.isTypeSupported(t)) || "";

      // ── Waveform analyser (separate AudioContext so it doesn't affect the record pipeline) ──
      try {
        const aCtx   = new AudioContext();
        const src    = aCtx.createMediaStreamSource(stream);
        const analyser = aCtx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.6;
        src.connect(analyser);
        analyserRef.current    = analyser;
        analyserCtxRef.current = aCtx;
        waveDataRef.current    = [];

        const buf          = new Uint8Array(analyser.frequencyBinCount);
        const WAVE_BARS    = 200; // rolling window — more bars = slower scroll
        const SAMPLE_MS    = 80;  // one sample every 80 ms → full window ≈ 16 s
        const BAR_GAP      = 0.4;
        let lastSampleTime = 0;

        const drawWave = (ts) => {
          if (ts - lastSampleTime >= SAMPLE_MS) {
            lastSampleTime = ts;
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) sum += (buf[i] - 128) ** 2;
            const rms = Math.min(1, Math.sqrt(sum / buf.length) / 12); // sensitive
            waveDataRef.current.push(rms);
            if (waveDataRef.current.length > WAVE_BARS)
              waveDataRef.current.shift(); // rolling — oldest drops off left
          }

          const canvas = waveCanvasRef.current;
          if (canvas) {
            const ctx = canvas.getContext("2d");
            const W = canvas.width, H = canvas.height;
            ctx.clearRect(0, 0, W, H);
            const samples = waveDataRef.current;
            const barW    = W / WAVE_BARS; // stays constant regardless of fill level
            for (let i = 0; i < samples.length; i++) {
              const amp = samples[i];
              const h   = Math.max(2, amp * (H - 4));
              ctx.fillStyle = `rgba(0,168,132,${(0.3 + amp * 0.7).toFixed(2)})`;
              ctx.fillRect(i * barW + BAR_GAP / 2, (H - h) / 2, Math.max(0.5, barW - BAR_GAP), h);
            }
          }
          waveAnimRef.current = requestAnimationFrame(drawWave);
        };
        requestAnimationFrame(drawWave);
      } catch { /* visualiser is non-critical */ }

      const mr = new MediaRecorder(recordStream, mimeType ? { mimeType } : {});
      mr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        audioCtx?.close();
        cancelAnimationFrame(waveAnimRef.current);
        analyserCtxRef.current?.close();
        analyserRef.current    = null;
        analyserCtxRef.current = null;
        waveDataRef.current    = [];
        const type = mr.mimeType || mimeType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type });
        setPendingAudio({ blob, url: URL.createObjectURL(blob), duration: recordingSecsRef.current });
        setIsRecording(false);
        setRecordingSecs(0);
        clearInterval(recordingTimerRef.current);
      };
      mr.start(100);
      mediaRecorderRef.current = mr;
      setIsRecording(true);
      setRecordingSecs(0);
      recordingTimerRef.current = setInterval(() => {
        recordingSecsRef.current += 1;
        setRecordingSecs(s => s + 1);
      }, 1000);
    } catch {
      message.error("Microphone access denied.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(recordingTimerRef.current);
  };

  const discardAudio = () => {
    if (pendingAudio?.url) URL.revokeObjectURL(pendingAudio.url);
    setPendingAudio(null);
  };

  const addFiles = (fileList) => {
    const accepted = [];
    const rejectedLines = [];

    Array.from(fileList).forEach(file => {
      const rule = WATI_FILE_RULES[file.type];
      if (!rule) {
        rejectedLines.push(`${file.name} — unsupported type`);
      } else if (file.size > rule.maxBytes) {
        const limit = rule.maxBytes >= MB
          ? `${rule.maxBytes / MB} MB`
          : `${rule.maxBytes / KB} KB`;
        rejectedLines.push(`${file.name} — exceeds ${limit} limit for ${rule.category}s`);
      } else {
        accepted.push(file);
      }
    });

    if (rejectedLines.length) {
      notification.warning({
        message: `${rejectedLines.length} file${rejectedLines.length > 1 ? "s" : ""} rejected`,
        description: rejectedLines.join("\n"),
        placement: "topRight",
        duration: 7,
      });
    }

    if (!accepted.length) return;
    const entries = accepted.map(file => ({
      id: uuidv4(),
      file,
      caption: "",
      previewUrl: (file.type.startsWith("image/") || file.type.startsWith("video/"))
        ? URL.createObjectURL(file) : null,
    }));
    setPendingFiles(prev => [...prev, ...entries]);
  };

  const removeFile = (id) => {
    setPendingFiles(prev => {
      const entry = prev.find(f => f.id === id);
      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      return prev.filter(f => f.id !== id);
    });
  };

  const updateFileCaption = (id, caption) => {
    setPendingFiles(prev => prev.map(f => f.id === id ? { ...f, caption } : f));
  };

  const getWatiType = (mimeType) => WATI_FILE_RULES[mimeType]?.category || "document";

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const sendFiles = async () => {
    if (!pendingFiles.length || isSendingFiles) return;
    setIsSendingFiles(true);
    const snapshot = [...pendingFiles];
    setPendingFiles([]);

    for (const f of snapshot) {
      const localMessageId = uuidv4();
      const tempId = `temp_${Date.now()}_${Math.random()}`;
      const watiType = getWatiType(f.file.type);

      const optimistic = {
        id: tempId,
        eventType: "message",
        type: watiType,
        text: f.caption || null,
        owner: true,
        statusString: "SENDING",
        timestamp: String(Math.floor(Date.now() / 1000)),
        operatorName: authState?.user?.name || null,
        _optimistic: true,
        _animate: true,
        _localPreviewUrl: f.previewUrl || null,
        data: f.previewUrl ? "_local_" : null, // non-null so MsgMediaContent renders
      };

      pendingTempMap.current.set(localMessageId, tempId);
      setMessages(prev => [...prev, optimistic]);

      try {
        const b64 = await toBase64(f.file);
        const userId = authState.id || authState?.user?.id || authState?.user?._id;
        const head   = { headers: { accessToken: localStorage.getItem("accessToken") } };

        const onUploadProgress = e => {
          if (e.total) setUploadProgress(prev => ({ ...prev, [tempId]: Math.round((e.loaded / e.total) * 100) }));
        };

        if (watiType === "image") {
          await axios.post(SEND_IMAGE_API, {
            whatsappId: chat.phone,
            b64Image: b64,
            localMessageId,
            userId,
            mimeType: f.file.type,
            caption: f.caption || "",
          }, { ...head, onUploadProgress });
        } else if (watiType === "video") {
          await axios.post(SEND_VIDEO_API, {
            whatsappId: chat.phone,
            b64Image: b64,
            localMessageId,
            userId,
            mimeType: f.file.type,
            caption: f.caption || "",
          }, { ...head, onUploadProgress });
        } else if (watiType === "document") {
          await axios.post(SEND_DOCUMENT_API, {
            whatsappId: chat.phone,
            b64Image: b64,
            localMessageId,
            userId,
            mimeType: f.file.type,
            caption: f.caption || "",
          }, { ...head, onUploadProgress });
        } else {
          throw new Error(`No API configured for type: ${watiType}`);
        }

        setUploadProgress(prev => { const n = { ...prev }; delete n[tempId]; return n; });
        setMessages(prev => prev.map(m => m.id === tempId ? { ...m, statusString: "SENT" } : m));
      } catch {
        setUploadProgress(prev => { const n = { ...prev }; delete n[tempId]; return n; });
        setMessages(prev => prev.map(m => m.id === tempId ? { ...m, statusString: "FAILED" } : m));
        pendingTempMap.current.delete(localMessageId);
      }

      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    }

    setIsSendingFiles(false);
  };

  const sendVoiceMessage = async () => {
    if (!pendingAudio?.blob || isSendingAudio) return;
    setIsSendingAudio(true);
    try {
      const buffer = await pendingAudio.blob.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = "";
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const b64Audio = btoa(binary);
      await axios.post(
        SEND_VOICE_API,
        { whatsappId: chat.phone, b64Audio, localMessageId: uuidv4(), userId: authState.id },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      discardAudio();
    } catch {
      message.error("Failed to send voice message.");
    } finally {
      setIsSendingAudio(false);
    }
  };

  const fmtRecording = s => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  useEffect(() => {
    if (activeTab !== "chat" || !scrollToMsgId) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`msg-${scrollToMsgId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedMsgId(scrollToMsgId);
        setTimeout(() => setHighlightedMsgId(null), 2000);
      }
      setScrollToMsgId(null);
    }, 80);
    return () => clearTimeout(timer);
  }, [activeTab, scrollToMsgId]); // eslint-disable-line

  const viewInChat = (msgId) => {
    setActiveTab("chat");
    setScrollToMsgId(msgId);
  };

  const loadCustomerQueries = async () => {
    if (!chat?.phone) return;
    setQueriesLoading(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/whatsapp/get_querysets",
        { from: "2000-01-01", to: dayjs().format("YYYY-MM-DD"), id: authState.id, search: chat.phone },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (res.data.success) setCustomerQueries(Array.isArray(res.data.data) ? res.data.data : []);
    } catch { setCustomerQueries([]); }
    finally { setQueriesLoading(false); }
  };

  useEffect(() => {
    if (activeTab !== "queries") return;
    loadCustomerQueries();
  }, [activeTab, chat?.phone]); // eslint-disable-line

  const mediaMessages = useMemo(() =>
    [...messages]
      .filter(m => m.eventType === "message" && ["image","video","audio","document"].includes((m.type||"").toLowerCase()))
      .reverse(),
    [messages]
  );
  const visualMedia = mediaMessages.filter(m => ["image","video","document"].includes((m.type||"").toLowerCase()));
  const otherMedia  = mediaMessages.filter(m => (m.type||"").toLowerCase() === "audio");

  return (
    <>
      {/* ── Tab bar ── */}
      <div style={{ display: "flex", background: theme.surface, borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
        {[["chat","Chat"], ["gallery", `Gallery${mediaMessages.length ? ` (${mediaMessages.length})` : ""}`], ["queries", `Queries${customerQueries.length ? ` (${customerQueries.length})` : ""}`]].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{
            flex: 1, padding: "9px 0", border: "none", background: "transparent", cursor: "pointer",
            fontSize: 13, fontWeight: activeTab === key ? 600 : 400,
            color: activeTab === key ? "#00a884" : theme.textSub,
            borderBottom: `2px solid ${activeTab === key ? "#00a884" : "transparent"}`,
            transition: "color 0.15s, border-color 0.15s",
          }}>{label}</button>
        ))}
      </div>

      {/* ── Chat panel ── */}
      <div
        style={{ flex: 1, position: "relative", overflow: "hidden", background: theme.bg, display: activeTab === "chat" ? "flex" : "none", flexDirection: "column" }}
        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false); }}
        onDrop={e => { e.preventDefault(); setIsDragOver(false); addFiles(e.dataTransfer.files); }}
      >
        {isDragOver && (
          <div style={{ position: "absolute", inset: 0, zIndex: 50, background: isDark ? "rgba(0,168,132,0.2)" : "rgba(0,168,132,0.13)", border: "3px dashed #00a884", borderRadius: 4, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <PaperClipOutlined style={{ fontSize: 48, color: "#00a884" }} />
            <div style={{ fontSize: 17, color: "#00a884", fontWeight: 700, marginTop: 14 }}>Drop files to attach</div>
          </div>
        )}
        <div style={{ position: "absolute", inset: 0, zIndex: 0, backgroundImage: `url(${require("../assets/bg-chat.png")})`, backgroundSize: "auto", backgroundRepeat: "repeat", backgroundPosition: "top left", filter: theme.bgFilter, opacity: theme.bgOpacity }} />
        <MediaScrollCtx.Provider value={scrollRef}>
        <div ref={scrollRef} onScroll={handleScroll} style={{ position: "relative", zIndex: 1, height: "100%", overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
          {loadingMsgs && messages.length > 0 && <div style={{ textAlign: "center", padding: "4px 0" }}><Spin size="small" /></div>}
          {loadingMsgs && messages.length === 0 && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}><Spin /></div>}
          {!loadingMsgs && messages.length === 0 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <Empty image={<WechatOutlined style={{ fontSize: 48, color: "#4a5568" }} />} imageStyle={{ height: 48 }} description={<span style={{ color: "#6b7280", fontSize: 13 }}>No messages yet</span>} />
            </div>
          )}
          {(() => {
            let prevDateKey = null;
            return messages.flatMap((item) => {
              const ts = item.timestamp ? dayjs(parseInt(item.timestamp) * 1000) : item.created ? dayjs(item.created) : null;
              const elements = [];
              if (ts) {
                const dateKey = ts.format("YYYY-MM-DD");
                if (dateKey !== prevDateKey) {
                  prevDateKey = dateKey;
                  const label = ts.isSame(dayjs(), "day") ? "Today" : ts.isSame(dayjs().subtract(1, "day"), "day") ? "Yesterday" : ts.format("DD MMM YYYY");
                  elements.push(
                    <div key={`sep-${dateKey}`} style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0" }}>
                      <div style={{ flex: 1, height: 1, background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)" }} />
                      <span style={{ fontSize: 10, color: theme.textSub, background: theme.pill, padding: "2px 10px", borderRadius: 20, fontWeight: 500, boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>{label}</span>
                      <div style={{ flex: 1, height: 1, background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)" }} />
                    </div>
                  );
                }
              }
              const isToday     = ts?.isSame(dayjs(), "day");
              const isYesterday = ts?.isSame(dayjs().subtract(1, "day"), "day");
              const timeStr = ts
                ? (isToday || isYesterday ? ts.format("hh:mm A") : fmtShortDateTime(ts))
                : "";
              if (item.eventType === "ticket") {
                elements.push(
                  <div key={item.id} style={{ display: "flex", justifyContent: "center", margin: "2px 0" }}>
                    <div style={{ background: theme.pill, borderRadius: 20, padding: "3px 12px", fontSize: 11, color: theme.textSub, textAlign: "center", maxWidth: "85%", boxShadow: "0 1px 2px rgba(0,0,0,0.2)" }}>
                      {item.eventDescription}
                      {timeStr && <span style={{ color: "#667781", marginLeft: 6, fontSize: 10 }}>{timeStr}</span>}
                    </div>
                  </div>
                );
                return elements;
              }
              if (item.eventType === "message") {
                const isOut       = item.owner === true;
                const isBroadcast = item.broadcastMessage === true || (item.type || "").toLowerCase() === "broadcastmessage";
                const msgType     = (item.type || "text").toLowerCase();
                const isMedia     = ["image", "video", "audio", "document", "sticker"].includes(msgType);
                const copyText = item.text || item.caption || "";
                // Support our optimistic replyTo, legacy replyMsg, and server replySourceMessage
                const quotedPreview = item.replyTo || item.replyMsg || item.replySourceMessage || null;
                elements.push(
                  <motion.div key={item.id} id={`msg-${item.id}`}
                    onDoubleClick={() => setQuotedMessage({ id: item.id, text: item.text || item.caption || "", type: item.type || "text", owner: item.owner })}
                    style={{ display: "flex", flexDirection: "column", alignItems: isOut ? "flex-end" : "flex-start", cursor: "default" }}
                    initial={item._animate ? { opacity: 0, x: isOut ? 28 : -28, scale: 0.93 } : false}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{ type: "spring", stiffness: 380, damping: 26, mass: 0.8 }}>
                    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", maxWidth: "80%", padding: msgType === "sticker" ? "4px" : isMedia && msgType !== "audio" ? "4px 4px 6px" : "7px 11px", borderRadius: isOut ? "12px 4px 12px 12px" : "4px 12px 12px 12px", backgroundColor: msgType === "sticker" ? "transparent" : isOut ? theme.bubbleOut : theme.bubbleIn, boxShadow: highlightedMsgId === item.id ? "0 0 0 3px #00a884" : msgType === "sticker" ? "none" : "0 1px 2px rgba(0,0,0,0.3)", transition: "box-shadow 0.4s", fontSize: 13, lineHeight: "1.5", color: theme.text }}>
                      {/* Quoted reply preview */}
                      {quotedPreview && (
                        <div style={{ borderLeft: "3px solid #00a884", background: "rgba(0,0,0,0.2)", borderRadius: "0 6px 6px 0", padding: "4px 8px", marginBottom: 6, width: "100%", boxSizing: "border-box", cursor: "pointer" }}
                          onClick={() => {
                            const refId = quotedPreview.id;
                            if (!refId) return;
                            const el = document.getElementById(`msg-${refId}`);
                            if (el) {
                              el.scrollIntoView({ behavior: "smooth", block: "center" });
                              setHighlightedMsgId(refId);
                              setTimeout(() => setHighlightedMsgId(null), 2000);
                            } else if (hasMoreRef.current) {
                              seekMsgIdRef.current = refId;
                              seekAttemptsRef.current = 0;
                              notification.info({ key: "seeking-msg", message: "Loading older messages…", description: "Looking for the quoted message.", placement: "topRight", duration: 0 });
                              fetchMessages(chat.conversationId, messages[0]?.id || null);
                            } else {
                              notification.warning({ message: "Message not found", placement: "topRight", duration: 2 });
                            }
                          }}>
                          <div style={{ fontSize: 11, color: "#00a884", fontWeight: 600, marginBottom: 1 }}>
                            {quotedPreview.owner === true ? "You" : (chat?.customer_name || "Them")}
                          </div>
                          <div style={{ fontSize: 11, color: "#8696a0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                            {quotedPreview.text || (
                              quotedPreview.type === "image"    ? "Photo" :
                              quotedPreview.type === "video"    ? "Video" :
                              quotedPreview.type === "audio"    ? "Voice message" :
                              quotedPreview.type === "document" ? "Document" : "(media)"
                            )}
                          </div>
                        </div>
                      )}
                      {isMedia && <MsgMediaContent item={item} />}
                      {!isMedia && item.text && (
                        <div>
                          <WaText text={item.text} onLinkClick={isOut ? null : url => setPendingLink(url)} />
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: theme.textSub, marginTop: 4, display: "flex", alignItems: "center", justifyContent: "flex-end", alignSelf: "stretch", gap: 4, paddingRight: isMedia && !["audio", "sticker"].includes(msgType) ? 6 : 0 }}>
                        {isOut && copyText && (
                          <CopyOutlined style={{ fontSize: 11, cursor: "pointer", color: "#667781" }}
                            onClick={() => { navigator.clipboard.writeText(copyText); message.success({ content: "Copied", duration: 1.5 }); }} />
                        )}
                        {isBroadcast && <span style={{ display: "inline-flex", alignItems: "center", gap: 2, color: "#b49ce7", fontWeight: 500 }}><NotificationOutlined style={{ fontSize: 9 }} /> Broadcast</span>}
                        {isOut && item.operatorName && <span style={{ fontStyle: "italic" }}>{item.operatorName}</span>}
                        <span>{timeStr}</span>
                        {isOut && (
                          item.statusString === "SENDING" && uploadProgress[item.id] !== undefined
                            ? <span style={{ fontSize: 10, color: theme.textSub, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{uploadProgress[item.id]}%</span>
                            : <MsgStatus status={item.statusString} />
                        )}
                        {isOut && item._optimistic && item.statusString === "FAILED" && (
                          <Tooltip title="Retry sending">
                            <ReloadOutlined
                              style={{ fontSize: 11, color: "#ff4d4f", cursor: "pointer", marginLeft: 1 }}
                              onClick={e => { e.stopPropagation(); retrySend(item); }}
                            />
                          </Tooltip>
                        )}
                        {!isOut && copyText && (
                          <CopyOutlined style={{ fontSize: 11, cursor: "pointer", color: "#667781" }}
                            onClick={() => { navigator.clipboard.writeText(copyText); message.success({ content: "Copied", duration: 1.5 }); }} />
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
                return elements;
              }
              return elements;
            });
          })()}
          <div ref={messagesEnd} />
        </div>
        </MediaScrollCtx.Provider>
      </div>

      {/* ── Gallery panel ── */}
      <MediaScrollCtx.Provider value={galleryScrollRef}>
      <div style={{ flex: 1, overflow: "hidden", background: theme.bg, display: activeTab === "gallery" ? "flex" : "none", flexDirection: "column" }}>
        {/* Filter bar */}
        {(() => {
          const chip = (key, icon, label, activeFilter, setFilter) => {
            const active = activeFilter === key;
            return (
              <Tooltip key={key} title={label}>
                <span onClick={() => setFilter(active ? null : key)}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", userSelect: "none", border: `1px solid ${active ? "#00a884" : theme.border}`, color: active ? "#00a884" : theme.textSub, background: active ? "rgba(0,168,132,0.12)" : "transparent", transition: "all 0.15s", flexShrink: 0 }}>
                  {icon}
                </span>
              </Tooltip>
            );
          };
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", borderBottom: `1px solid ${theme.border}`, flexShrink: 0, flexWrap: "wrap" }}>
              {chip("sent",     <ArrowUpOutlined    style={{ fontSize: 14, color: galleryOwnerFilter === "sent"     ? "#00a884" : "#25d366" }} />, "Sent",     galleryOwnerFilter, setGalleryOwnerFilter)}
              {chip("received", <ArrowDownOutlined  style={{ fontSize: 14, color: galleryOwnerFilter === "received" ? "#00a884" : "#8696a0" }} />, "Received", galleryOwnerFilter, setGalleryOwnerFilter)}
              <span style={{ width: 1, height: 16, background: theme.border, flexShrink: 0 }} />
              {chip("image",    <PictureOutlined    style={{ fontSize: 14 }} />, "Images",   galleryTypeFilter, setGalleryTypeFilter)}
              {chip("video",    <PlayCircleOutlined style={{ fontSize: 14 }} />, "Videos",   galleryTypeFilter, setGalleryTypeFilter)}
              {chip("document", <FileOutlined       style={{ fontSize: 14 }} />, "Docs",     galleryTypeFilter, setGalleryTypeFilter)}
              {chip("audio",    <AudioOutlined      style={{ fontSize: 14 }} />, "Audio",    galleryTypeFilter, setGalleryTypeFilter)}
            </div>
          );
        })()}
        {/* Scrollable grid */}
        <div ref={galleryScrollRef} style={{ flex: 1, overflowY: "auto", padding: 2 }}>
          {(() => {
            let items = mediaMessages;
            if (galleryOwnerFilter === "sent")     items = items.filter(m => m.owner === true);
            if (galleryOwnerFilter === "received") items = items.filter(m => m.owner !== true);
            if (galleryTypeFilter)                 items = items.filter(m => (m.type || "").toLowerCase() === galleryTypeFilter);
            if (items.length === 0) return (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
                <Empty image={<PictureOutlined style={{ fontSize: 40, color: "#4a5568" }} />} imageStyle={{ height: 40 }} description={<span style={{ color: "#6b7280", fontSize: 13 }}>{mediaMessages.length === 0 ? "No media yet" : "No matches"}</span>} />
              </div>
            );
            return (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                {items.map(m => <GalleryThumb key={m.id} item={m} onViewInChat={viewInChat} />)}
              </div>
            );
          })()}
        </div>
      </div>
      </MediaScrollCtx.Provider>

      {/* ── Queries panel ── */}
      {activeTab === "queries" && (
        <div style={{ flex: 1, overflowY: "auto", background: theme.bg, padding: 10 }}>
          {queriesLoading
            ? <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spin /></div>
            : customerQueries.length === 0
              ? <Empty description={<span style={{ color: "#8696a0" }}>No queries found</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 32 }} />
              : (() => {
                  // Group by status using PIPELINE_COLUMNS ordering
                  const grouped = {};
                  customerQueries.forEach(q => {
                    const key = q.status || "Unknown";
                    if (!grouped[key]) grouped[key] = [];
                    grouped[key].push(q);
                  });
                  // Order groups by PIPELINE_COLUMNS, then any remaining
                  const orderedKeys = [];
                  PIPELINE_COLUMNS.forEach(col => {
                    col.statuses.forEach(s => { if (grouped[s] && !orderedKeys.includes(s)) orderedKeys.push(s); });
                  });
                  Object.keys(grouped).forEach(k => { if (!orderedKeys.includes(k)) orderedKeys.push(k); });

                  return orderedKeys.map(status => {
                    const col = PIPELINE_COLUMNS.find(c => c.statuses.includes(status));
                    const color = col?.color || "#8696a0";
                    const queries = grouped[status];
                    return (
                      <div key={status} style={{ marginBottom: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0, display: "inline-block" }} />
                          <span style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: 0.5 }}>{status}</span>
                          <span style={{ fontSize: 10, color: "#8696a0" }}>({queries.length})</span>
                        </div>
                        {queries.map(q => (
                          <div key={q.query_id} style={{ background: theme.surface, borderRadius: 8, padding: "8px 10px", marginBottom: 6, borderLeft: `3px solid ${color}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                              <span style={{ fontSize: 10, color: "#8696a0" }}>#{q.query_id}</span>
                              <span style={{ fontSize: 10, color: "#8696a0", flexShrink: 0 }}>
                                {q.date ? dayjs(q.date).format("DD MMM YYYY") : ""}
                              </span>
                            </div>
                            {(q.make || q.model) && (
                              <div style={{ fontSize: 12, fontWeight: 600, color: theme.text, marginBottom: 3 }}>
                                {[q.make, q.model, q.year].filter(Boolean).join(" · ")}
                              </div>
                            )}
                            {q.body_part && (
                              <div style={{ fontSize: 11, color: "#8696a0", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {q.body_part}
                              </div>
                            )}
                            {q.price_given > 0 && (
                              <Tag color="green" style={{ fontSize: 10, margin: 0 }}>AED {q.price_given}</Tag>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  });
                })()
          }
        </div>
      )}

      {activeTab === "chat" && (
        <div style={{ borderTop: `1px solid ${theme.border}`, flexShrink: 0, background: theme.surface }}>
          {/* Quoted message bar */}
          {quotedMessage && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderBottom: `1px solid ${theme.border}`, background: theme.pill }}>
              <div style={{ flex: 1, borderLeft: "3px solid #00a884", paddingLeft: 8, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "#00a884", fontWeight: 600 }}>
                  {quotedMessage.owner === true ? "You" : (chat?.customer_name || "Them")}
                </div>
                <div style={{ fontSize: 12, color: "#8696a0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {quotedMessage.text || "(media)"}
                </div>
              </div>
              <Button type="text" size="small" icon={<CloseCircleOutlined />}
                onClick={() => setQuotedMessage(null)} style={{ color: "#8696a0", flexShrink: 0 }} />
            </div>
          )}
          {/* Pending audio preview */}
          {pendingAudio && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: `1px solid ${theme.border}` }}>
              <audio
                src={pendingAudio.url}
                controls
                style={{ flex: 1, height: 40, minWidth: 0 }}
                onLoadedMetadata={e => {
                  const el = e.currentTarget;
                  if (el.duration === Infinity || isNaN(el.duration)) {
                    el.currentTime = pendingAudio.duration || 9999;
                    el.addEventListener("timeupdate", function fix() {
                      el.removeEventListener("timeupdate", fix);
                      el.currentTime = 0;
                    });
                  }
                }}
              />
              <Tooltip title="Discard">
                <Button size="small" danger icon={<CloseCircleOutlined />} onClick={discardAudio} style={{ flexShrink: 0 }} />
              </Tooltip>
            </div>
          )}
          {/* Pending file attachments */}
          {pendingFiles.length > 0 && (
            <div style={{ display: "flex", gap: 10, padding: "10px 12px", borderBottom: `1px solid ${theme.border}`, flexWrap: "wrap", background: theme.surface }}>
              {pendingFiles.map(f => (
                <div key={f.id} style={{ position: "relative", flexShrink: 0, display: "flex", flexDirection: "column", gap: 5, width: 96 }}>
                  {f.previewUrl && f.file.type.startsWith("image/") ? (
                    <img src={f.previewUrl} alt="" style={{ width: 96, height: 72, objectFit: "cover", borderRadius: 7, display: "block" }} />
                  ) : f.previewUrl && f.file.type.startsWith("video/") ? (
                    <video src={f.previewUrl} style={{ width: 96, height: 72, objectFit: "cover", borderRadius: 7, display: "block" }} muted />
                  ) : (
                    <div style={{ width: 96, height: 72, borderRadius: 7, background: theme.pill, border: `1px solid ${theme.border}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3 }}>
                      <FileOutlined style={{ fontSize: 24, color: theme.textSub }} />
                      <span style={{ fontSize: 9, color: theme.textSub, maxWidth: 88, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "0 4px" }}>
                        {f.file.name.split(".").pop().toUpperCase()}
                      </span>
                    </div>
                  )}
                  <Input
                    size="small"
                    placeholder="Caption…"
                    value={f.caption}
                    onChange={e => updateFileCaption(f.id, e.target.value)}
                    style={{ fontSize: 11, borderRadius: 5, background: theme.input, borderColor: theme.border, color: theme.text, width: "100%" }}
                  />
                  <button
                    onClick={() => removeFile(f.id)}
                    style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", border: "none", background: "#ff4d4f", color: "white", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1, zIndex: 1 }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
          {/* Hidden file picker */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={WATI_ACCEPT_ATTR}
            style={{ display: "none" }}
            onChange={e => { if (e.target.files?.length) { addFiles(e.target.files); e.target.value = ""; } }}
          />
          <div style={{ padding: "10px 12px", display: "flex", gap: 8, alignItems: "center" }}>
            {!isRecording && (
              <Tooltip title={WATI_ACCEPT_LABEL} placement="topLeft">
                <Button
                  type="text"
                  icon={<PaperClipOutlined />}
                  onClick={() => fileInputRef.current?.click()}
                  style={{ flexShrink: 0, height: 36, borderRadius: 8, color: theme.textSub, fontSize: 16 }}
                />
              </Tooltip>
            )}
            {isRecording ? (
              /* Recording row — replaces the text input */
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, background: theme.input, borderRadius: 8, padding: "6px 12px", height: 36 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#ff4d4f", flexShrink: 0, animation: "pulse 1s infinite" }} />
                <span style={{ fontSize: 13, color: theme.text, fontVariantNumeric: "tabular-nums", fontWeight: 600, letterSpacing: 0.5, flexShrink: 0 }}>{fmtRecording(recordingSecs)}</span>
                <canvas ref={waveCanvasRef} width={240} height={24}
                  style={{ flex: 1, height: 24, display: "block" }} />
              </div>
            ) : (
              <div style={{ flex: 1 }} className={inputLaunching ? "chat-input-launch" : undefined}>
              <Input.TextArea
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                placeholder="Type a message…"
                autoSize={{ minRows: 1, maxRows: 4 }}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                style={{ borderRadius: 8, resize: "none", fontSize: 13, background: theme.input, borderColor: theme.border, color: theme.text, width: "100%" }}
              />
              </div>
            )}
            {messageText.trim() || pendingAudio || pendingFiles.length > 0 ? (
              <Button type="primary" icon={<SendOutlined />} loading={isSendingAudio || isSendingFiles} onClick={pendingAudio ? sendVoiceMessage : pendingFiles.length > 0 ? sendFiles : handleSend} style={{ flexShrink: 0, height: 36, borderRadius: 8 }} />
            ) : (
              <Tooltip title={isRecording ? "Stop recording" : isFirefox ? "Firefox doesn't support voice recording properly, use Chrome/Edge for better results." : "Record voice message"} placement="top">
                <Button
                  type={isRecording ? "primary" : "default"}
                  danger={isRecording}
                  icon={isRecording ? <AudioMutedOutlined /> : <AudioOutlined />}
                  onClick={isRecording ? stopRecording : startRecording}
                  style={{ flexShrink: 0, height: 36, borderRadius: 8, ...(isRecording ? {} : { background: "rgba(0,168,132,0.12)", borderColor: "#00a884", color: "#00a884" }) }}
                />
              </Tooltip>
            )}
          </div>
        </div>
      )}
      <Modal
        open={!!pendingLink}
        title="⚠️ External link"
        onOk={() => { window.open(pendingLink.startsWith("http") ? pendingLink : `https://${pendingLink}`, "_blank", "noopener,noreferrer"); setPendingLink(null); }}
        onCancel={() => setPendingLink(null)}
        okText="Open link"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        width={420}
      >
        <p style={{ marginBottom: 8 }}>This link was received from the customer and <strong>may not be secure</strong>.</p>
        <p style={{ wordBreak: "break-all", background: "#f5f5f5", padding: "6px 10px", borderRadius: 6, fontSize: 12, marginBottom: 8 }}>{pendingLink}</p>
        <p style={{ color: "#888", fontSize: 12, margin: 0 }}>Only open links from sources you trust.</p>
      </Modal>
    </>
  );
}

// ── Active Chat Drawer ────────────────────────────────────────────────────────
const DRAWER_MIN = 400;
const DRAWER_MAX = Math.round(DRAWER_MIN * 1.8); // 720

function ActiveChatDrawer({ chat, pinned, onPinToggle, onClose, onWidthChange, onChatPatch }) {
  const isMobile = useIsMobile();
  const [drawerWidth, setDrawerWidth] = useState(DRAWER_MIN);
  const [resizing, setResizing] = useState(false);
  const [isDark, setIsDark] = useState(() => localStorage.getItem("wati_chat_theme") !== "light");

  const toggleTheme = () => setIsDark(d => {
    const next = !d;
    localStorage.setItem("wati_chat_theme", next ? "dark" : "light");
    return next;
  });

  const handleResizeStart = (e) => {
    e.preventDefault();
    const startX     = e.clientX;
    const startWidth = drawerWidth;
    setResizing(true);

    const onMove = (e) => {
      const next = Math.min(DRAWER_MAX, Math.max(DRAWER_MIN, startWidth + (startX - e.clientX)));
      setDrawerWidth(next);
      onWidthChange?.(next);
    };
    const onUp = () => {
      setResizing(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const theme = ct(isDark);

  return (
    <ChatThemeCtx.Provider value={isDark}>
    <Drawer open={!!chat} onClose={onClose} mask={!pinned} maskClosable={!pinned} closable={false}
      push={false}
      width={isMobile ? "100%" : drawerWidth} placement="right"
      styles={{
        header: { padding: "10px 16px", borderBottom: `1px solid ${theme.border}`, background: theme.surface, transition: "background 0.35s ease, border-color 0.35s ease" },
        body: { padding: 0, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", background: theme.bg, position: "relative", userSelect: resizing ? "none" : undefined, transition: "background 0.35s ease" },
      }}
      title={chat && <ChatPanelHeader chat={chat} pinned={pinned} onPinToggle={onPinToggle} onClose={onClose} isMobile={isMobile} onChatPatch={onChatPatch} isDark={isDark} onThemeToggle={toggleTheme} />}>
      {!isMobile && (
        <div
          onMouseDown={handleResizeStart}
          style={{
            position: "absolute", left: 0, top: 0, bottom: 0, width: 6,
            cursor: "col-resize", zIndex: 10,
            background: resizing ? "rgba(255,255,255,0.08)" : "transparent",
            transition: "background 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
          onMouseLeave={e => { if (!resizing) e.currentTarget.style.background = "transparent"; }}
        />
      )}
      <div className="cpt" style={{ display: "contents" }}>
        {chat && <ChatPanelMessages chat={chat} />}
      </div>
    </Drawer>
    </ChatThemeCtx.Provider>
  );
}

const URGENCY_CFG = {
  1: { color: "#595959", pill: "#f0f0f0",  cardBg: "transparent",              label: "1 · Low"      },
  2: { color: "#1677ff", pill: "#e6f4ff",  cardBg: "rgba(22,119,255,0.04)",    label: "2 · Normal"   },
  3: { color: "#d48806", pill: "#fffbe6",  cardBg: "rgba(250,173,20,0.06)",    label: "3 · Medium"   },
  4: { color: "#d4380d", pill: "#fff2e8",  cardBg: "rgba(255,122,0,0.08)",     label: "4 · High"     },
  5: { color: "#cf1322", pill: "#fff1f0",  cardBg: "rgba(255,77,79,0.12)",     label: "5 · Critical" },
};

function QueryCard({ query, columnColor, showReason, onCardClick, onWaClick, slNo, carLogoTypes }) {
  const [urgency, setUrgency] = useState(query.urgency || null);

  const updateUrgency = async (val) => {
    const prev = urgency;
    setUrgency(val ?? null);
    try {
      await axios.post(
        API_DOMAIN + "/whatsapp/update_urgency",
        { query_id: query.query_id, urgency: val ?? null },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
    } catch { setUrgency(prev); }
  };

  const createdDate =
    query?.timeline?.length > 0 && query.timeline[0]?.query_created
      ? dayjs(query.timeline[0].query_created, "YYYY-MM-DD hh:mm:ss A")
      : null;

  const lastEntry = query?.timeline?.length > 0 ? query.timeline[query.timeline.length - 1] : null;
  const lastTime  = lastEntry ? dayjs(Object.values(lastEntry)[0], "YYYY-MM-DD hh:mm:ss A") : null;
  let elapsedStr  = null;
  let elapsedColor = "#8c8c8c";
  if (lastTime?.isValid()) {
    const totalMins = dayjs().diff(lastTime, "minute");
    elapsedStr  = `${String(Math.floor(totalMins / 60)).padStart(2, "0")}:${String(totalMins % 60).padStart(2, "0")}`;
    elapsedColor = totalMins < 60 ? "#52c41a" : totalMins < 240 ? "#faad14" : "#ff4d4f";
  }

  const logoKey = query.make?.toLowerCase().replaceAll(" ", "-");
  const logoUri = carLogoTypes?.[logoKey]?.logotype?.uri;

  const suppliersReached = (() => {
    try { return JSON.parse(query.moredetails?.success || "[]"); } catch { return []; }
  })();
  const waSent = Array.isArray(suppliersReached) && suppliersReached.length > 0;
  const cardCountry = getCardCountry(query.phone);
  const isIntl = cardCountry && cardCountry.name !== "United Arab Emirates";

  return (
    <Card
      hoverable
      onClick={onCardClick}
      size="small"
      style={{
        borderRadius: 6,
        borderLeft: `4px solid ${urgency ? URGENCY_CFG[urgency].color : columnColor}`,
        borderRight: isIntl ? "4px solid #1677ff" : undefined,
        backgroundColor: urgency ? URGENCY_CFG[urgency].cardBg : undefined,
        cursor: "pointer",
        fontSize: 12,
        position: "relative",
        transition: "background-color 0.2s, border-color 0.2s",
      }}
      styles={{ body: { padding: "8px 10px" } }}
    >
      {/* Globe watermark — sits behind all card content */}
      {isIntl && (
        <GlobalOutlined style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: 120, color: "rgba(22,119,255,0.14)", pointerEvents: "none", zIndex: 0 }} />
      )}

      {/* Card content — sits above watermark */}
      <div style={{ position: "relative", zIndex: 1 }}>

      {/* Serial No + Query ID | Date + Elapsed */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 4 }}>
        {/* Left: slNo + id + WA */}
        <span style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
          {slNo != null && (
            <span style={{
              background: "#e6e6e6", color: "#595959",
              borderRadius: "50%", minWidth: 18, height: 18,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontSize: 10, fontWeight: 700, flexShrink: 0,
            }}>
              {slNo}
            </span>
          )}
          <span style={{ fontWeight: 700, color: "#1677ff" }}>#{query.query_id}</span>
          <Tooltip title={waSent ? `Sent to ${suppliersReached.length} supplier(s) via WhatsApp` : "Not sent to suppliers yet"}>
            <WhatsAppOutlined
              style={{ fontSize: 13, color: waSent ? "#25d366" : "#d9d9d9", cursor: "pointer" }}
              onClick={(e) => { e.stopPropagation(); onWaClick && onWaClick(); }}
            />
          </Tooltip>
        </span>

        {/* Centre: urgency selector */}
        <Select
          size="small"
          value={urgency}
          onChange={val => updateUrgency(val)}
          onClick={e => e.stopPropagation()}
          allowClear
          onClear={() => updateUrgency(null)}
          style={{ width: 80 }}
          dropdownStyle={{ minWidth: 140 }}
          variant="borderless"
          suffixIcon={null}
          styles={{
            selector: {
              height: 22, padding: "0 6px",
              display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: 20,
              background: urgency ? URGENCY_CFG[urgency].pill : "transparent",
              border: urgency ? `2px solid ${URGENCY_CFG[urgency].color}` : "1.5px solid #d9d9d9",
              transition: "all 0.2s",
            }
          }}
          options={Object.entries(URGENCY_CFG).map(([v, cfg]) => ({
            value: Number(v),
            label: (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.color, flexShrink: 0, display: "inline-block" }} />
                <span style={{ color: cfg.color, fontWeight: 600, fontSize: 11 }}>{cfg.label}</span>
              </span>
            ),
          }))}
          labelRender={({ value }) => value
            ? <span style={{ color: URGENCY_CFG[value]?.color, fontWeight: 700, fontSize: 11, display: "inline-flex", alignItems: "center", gap: 3 }}>
                {value === 5 ? <><FireOutlined style={{ fontSize: 10 }} /> Critical</> : URGENCY_CFG[value]?.label}
              </span>
            : <span style={{ color: "#bfbfbf", fontSize: 10, letterSpacing: 0.3 }}>urgency</span>
          }
        />

        {/* Right: date */}
        {createdDate?.isValid() && (
          <span style={{ color: "#8c8c8c", fontSize: 11, flexShrink: 0 }}>
            <CalendarOutlined style={{ marginRight: 3 }} />
            {createdDate.format("DD-MM-YY")}
          </span>
        )}
      </div>

      {/* Agent + elapsed timer on same row */}
      {(query.user_id || elapsedStr) && (
        <div style={{ marginBottom: 4, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 5, minWidth: 0 }}>
          {query.user_id && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, flex: 1 }}>
              <Avatar size={18} style={{ backgroundColor: getAgentColor(query.user_id), fontSize: 9, flexShrink: 0 }}>
                {agentInitials(query.user_id)}
              </Avatar>
              <span style={{ fontSize: 11, fontWeight: 600, color: getAgentColor(query.user_id), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                {query.user_id}
              </span>
            </div>
          )}
          {elapsedStr && (
            <span style={{ fontSize: 11, color: elapsedColor, display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
              <ClockCircleOutlined />
              {elapsedStr} hrs
            </span>
          )}
        </div>
      )}

      {/* Vehicle (logo+model bordered) + Customer stacked */}
      {(logoUri || query.make || query.model || query.customer_info?.name || query.phone || query.body_part) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 4, alignItems: "stretch" }}>
          {/* Left: bordered rounded box — logo + model, 5 rows tall */}
          {(logoUri || query.make || query.model) && (
            <Tooltip title={query.make || ""}>
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                flexShrink: 0, width: 58,
                border: "1px solid #e0e0e0", borderRadius: 8,
                padding: "5px 4px", gap: 4,
                minHeight: 90,
              }}>
                {logoUri
                  ? <img src={logoUri} alt="" style={{ width: 38, height: 38, objectFit: "contain" }} />
                  : query.make && <span style={{ fontWeight: 700, fontSize: 11, textAlign: "center", lineHeight: 1.2 }}>{query.make}</span>
                }
                {query.model && (
                  <span style={{ fontSize: 9, color: "#595959", textAlign: "center", lineHeight: 1.2, wordBreak: "break-word", maxWidth: 50 }}>
                    {query.model}
                  </span>
                )}
              </div>
            </Tooltip>
          )}

          {/* Right column: customer box on top, body part below */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
            {(query.customer_info?.name || query.phone) && (
              <div style={{
                flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 3,
                border: "1px solid #e0e0e0", borderRadius: 8, padding: "4px 7px",
              }}>
                {/* Country flag row */}
                {cardCountry && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <img src={`https://cdn.clare.ai/flags/${cardCountry.code}.svg`} alt={cardCountry.name} style={{ width: 14, height: 14, objectFit: "contain", borderRadius: 2, flexShrink: 0 }} />
                    <span style={{ fontSize: 9, color: "#000", fontWeight: isIntl ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {cardCountry.name}
                    </span>
                  </div>
                )}
                {query.customer_info?.name && (
                  <div style={{ display: "flex", alignItems: "center", gap: 3, minWidth: 0 }}>
                    <UserOutlined style={{ fontSize: 10, color: "#8c8c8c", flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                      {query.customer_info.name}
                    </span>
                  </div>
                )}
                {query.phone && (
                  <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <PhoneOutlined style={{ fontSize: 10, color: "#8c8c8c", flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: "#595959", whiteSpace: "nowrap" }}><CopyablePhone phone={query.phone} /></span>
                  </div>
                )}
              </div>
            )}
            {query.body_part && (
              <Tag style={{ fontSize: 11, margin: 0, width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", boxSizing: "border-box" }}>
                {query.body_part}
              </Tag>
            )}
          </div>
        </div>
      )}

      {/* Price — right-aligned chip */}
      {query.price_given > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
          <Tag color="green" style={{ fontWeight: 700, fontSize: 12, margin: 0, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <DirhamSymbol size="1em" weight="bold" />
            {query.price_given}
          </Tag>
        </div>
      )}

      {/* Payment status chip */}
      {(query.status === "Confirmed" || query.status === "Confirmed & Paid") && (
        <div style={{ marginTop: 4 }}>
          {query.status === "Confirmed & Paid"
            ? <Tag color="green" style={{ fontSize: 11 }}>Paid</Tag>
            : <Tag color="orange" style={{ fontSize: 11 }}>Payment Pending</Tag>
          }
        </div>
      )}

      {/* Lost reason */}
      {showReason && query.status && (
        <div style={{ marginTop: 4 }}>
          <Tag color="red" style={{ fontSize: 11 }}>{query.status}</Tag>
        </div>
      )}

      </div>{/* end content wrapper */}
    </Card>
  );
}

function DetailModal({ query, onClose }) {
  if (!query) return null;

  const createdDate =
    query?.timeline?.length > 0 && query.timeline[0]?.query_created
      ? dayjs(query.timeline[0].query_created, "YYYY-MM-DD hh:mm:ss A").format("DD-MM-YYYY hh:mm A")
      : null;

  const statusColor = () => {
    if (["Confirmed", "Confirmed & Paid", "Delivered"].includes(query.status)) return "green";
    if (["Cancelled", "Customer Declined", "Unavailable", "Query Lost", "Other"].includes(query.status)) return "red";
    if (query.status === "Waiting for Price") return "default";
    if (query.status === "Waiting for confirmation") return "gold";
    if (query.status === "Out for delivery") return "orange";
    return "blue";
  };

  const field = (label, value) =>
    value != null && value !== "" && value !== 0 ? (
      <div style={{ marginBottom: 10 }}>
        <div style={{ color: "#8c8c8c", fontSize: 11, marginBottom: 2 }}>{label}</div>
        <div style={{ fontWeight: 500, fontSize: 13 }}>{value}</div>
      </div>
    ) : null;

  return (
    <Modal
      open={!!query}
      onCancel={onClose}
      footer={null}
      title={
        <span>
          Query{" "}
          <span style={{ color: "#1677ff" }}>#{query.query_id}</span>
          <Tag style={{ marginLeft: 12 }} color={statusColor()}>
            {query.status}
          </Tag>
        </span>
      }
      width={580}
      style={{ top: 16 }}
      styles={{ body: { height: "calc(100vh - 130px)", overflowY: "auto", paddingRight: 4 } }}
    >
      <Section label="Query Info">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
          {field("Query ID", query.query_id)}
          {field("Date Created", createdDate)}
          {field("User", query.user_id)}
        </div>
      </Section>

      <Section label="Vehicle">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
          {field("Make", query.make)}
          {field("Model", query.model)}
          {field("Body Part", query.body_part)}
          {field("Model Details", query.model_details)}
          {field("Year", query.moredetails?.year)}
          {field("Chassis No", query.chassisno)}
          {field("Cylinders", query.cylinder)}
        </div>
      </Section>

      <Section label="Customer">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
          {field("Name", query.customer_info?.name)}
          {field("Phone", query.phone)}
        </div>
      </Section>

      <Section label="Pricing">
        {!query.price_given && !query.best_price_received && !query.delivery_cost && !query.supplier ? (
          <div style={{ color: "#bfbfbf", fontSize: 13, fontStyle: "italic" }}>No price information available.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            {field("Selling Price", query.price_given ? `${query.price_given} AED` : null)}
            {field("Best Cost Price", query.best_price_received ? `${query.best_price_received} AED` : null)}
            {field("Delivery Cost", query.delivery_cost ? `${query.delivery_cost} AED` : null)}
            {field("Supplier", query.supplier)}
          </div>
        )}
      </Section>

      {query.moredetails?.remarks && (
        <Section label="Remarks">
          <div style={{ fontSize: 13, whiteSpace: "pre-wrap", color: "#262626" }}>
            {query.moredetails.remarks}
          </div>
        </Section>
      )}

      {query.timeline?.length > 0 && (
        <Section label="Timeline">
          <Timeline
            mode="left"
            style={{ fontSize: 12, marginTop: 8 }}
            items={[...query.timeline].reverse().map(item => {
              const key   = Object.keys(item)[0];
              const value = item[key];
              return { children: <span><b>{key.replace(/_/g, " ")}</b> — {value}</span> };
            })}
          />
        </Section>
      )}
    </Modal>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: "#8c8c8c", fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </div>
      <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: "10px 12px" }}>
        {children}
      </div>
    </div>
  );
}

function SuppliersModal({ query, onClose }) {
  if (!query) return null;
  let suppliers = [];
  try { suppliers = JSON.parse(query.moredetails?.success || "[]"); } catch (_) {}

  const dataSource = suppliers.map((s, i) => ({
    key: i,
    no: i + 1,
    phone: (s?.phoneNumber || s || "").toString().replace("@c.us", ""),
  }));

  const columns = [
    { title: "#", dataIndex: "no", key: "no", width: 50, align: "center" },
    { title: "Phone Number", dataIndex: "phone", key: "phone" },
  ];

  return (
    <Modal
      open={!!query}
      onCancel={onClose}
      footer={null}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <WhatsAppOutlined style={{ color: "#25d366" }} />
          Suppliers Reached —{" "}
          <span style={{ color: "#1677ff" }}>#{query.query_id}</span>
        </span>
      }
      width={420}
      style={{ top: 16 }}
      styles={{ body: { height: "calc(100vh - 130px)", overflowY: "auto", paddingRight: 4 } }}
    >
      {dataSource.length === 0 ? (
        <div style={{ textAlign: "center", color: "#bfbfbf", padding: "32px 0", fontSize: 13 }}>
          No suppliers were reached for this query.
        </div>
      ) : (
        <Table
          dataSource={dataSource}
          columns={columns}
          size="small"
          pagination={false}
          bordered
          style={{ borderRadius: 8, overflow: "hidden" }}
        />
      )}
    </Modal>
  );
}

const PIPELINE_COLUMNS = [
  { key: "in_chat",           label: "Active Chats",             color: "#1677ff", icon: <img src={watiLogo} alt="WATI" style={{ width: 15, height: 15, objectFit: "contain", display: "inline-block" }} />, statuses: [], isChat: true },
  { key: "waiting_for_price", label: "Waiting for Price",        color: "#8c8c8c", icon: <TagOutlined />,           statuses: ["Waiting for Price"] },
  { key: "waiting_conf",      label: "Waiting for Confirmation", color: "#faad14", icon: <HourglassOutlined />,     statuses: ["Waiting for confirmation"] },
  { key: "confirmed",         label: "Confirmed",                color: "#52c41a", icon: <CheckCircleOutlined />,   statuses: ["Confirmed", "Confirmed & Paid"] },
  { key: "delivery",          label: "Delivery",                 color: "#00b96b", icon: <CarOutlined />,           statuses: ["Out for delivery", "Delivered"] },
  { key: "lost",              label: "Lost",                     color: "#ff4d4f", icon: <CloseCircleOutlined />,   statuses: ["Cancelled", "Customer Declined", "Unavailable", "Query Lost", "Other"] },
  { key: "returns_refunds",   label: "Returns / Refunds",        color: "#722ed1", icon: <RollbackOutlined />,      statuses: [], placeholder: true,  placeholderNote: "Coming soon" },
];

function groupQueriesByStatus(queries) {
  return PIPELINE_COLUMNS.reduce((acc, col) => {
    acc[col.key] = (col.isChat || col.placeholder) ? [] : queries.filter((q) => col.statuses.includes(q.status));
    return acc;
  }, {});
}

function QuerysetV4() {
  const { authState } = useContext(AuthContext);
  const head = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const [querySet, setQuerySet] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [selectedSupplierQuery, setSelectedSupplierQuery] = useState(null);
  const [groupByAgent, setGroupByAgent] = useState(false);
  const [collapseStates, setCollapseStates] = useState({});
  const [, setTick] = useState(0);
  const [staleSortOn, setStaleSortOn]               = useState(false);
  const [internationalSortOn, setInternationalSortOn] = useState(false);
  const [highValueSortOn, setHighValueSortOn]       = useState(false);
  const [urgencySortOn, setUrgencySortOn]           = useState(false);
  const [readReceiptsOn, setReadReceiptsOn]         = useState(() => localStorage.getItem("wati_read_receipts") !== "false");
  const [carLogoTypes, setCarLogoTypes]         = useState({});
  const [activeChats, setActiveChats]           = useState([]);
  const [chatsLoading, setChatsLoading]         = useState(false);
  const [chatsLoadingMore, setChatsLoadingMore] = useState(false);
  const [hasMoreChats, setHasMoreChats]         = useState(true);
  const [chatSearch, setChatSearch]             = useState("");
  const [chatSearchFilter, setChatSearchFilter] = useState(0);
  const [starredFilter, setStarredFilter]       = useState(false);
  const [statusFilter, setStatusFilter]         = useState(null); // null = all, or ticket_status number
  const [expirySortOn, setExpirySortOn]         = useState(false);
  const [selectedChat, setSelectedChat]         = useState(null);
  const [chatDrawerPinned, setChatDrawerPinned] = useState(false);
  const [chatDrawerWidth, setChatDrawerWidth]   = useState(DRAWER_MIN);
  const boardRef = useRef(null);
  const chatScrollRef = useRef(null);

  const { watiChatUpdates } = useWatiSocket();
  const watiProcessedCountRef = useRef(0);
  const [flashingChatIds, setFlashingChatIds] = useState(new Set());
  const selectedChatRef = useRef(null);
  useEffect(() => { selectedChatRef.current = selectedChat?.id ?? null; }, [selectedChat]);

  useEffect(() => {
    if (!selectedChat?.id) return;
    if (localStorage.getItem("wati_read_receipts") === "false") return;
    setActiveChats(prev => prev.map(c => c.id === selectedChat.id ? { ...c, unread_count: 0 } : c));
  }, [selectedChat?.id]);

  useEffect(() => {
    if (watiChatUpdates.length <= watiProcessedCountRef.current) return;
    const newUpdates = watiChatUpdates.slice(watiProcessedCountRef.current);
    watiProcessedCountRef.current = watiChatUpdates.length;

    newUpdates.forEach(update => {
      // WATI hub protocol sends some events as arrays: [{type, target, arguments}]
      if (update.target === "catchSelectedConversation") {
        const chatId = update["0"];
if (chatId) {
          setActiveChats(prev => prev.map(c => c.conversationId === chatId ? { ...c, unread_count: 0 } : c));
        }
        return;
      }

      if (update.target !== "sendTextMessage") return;
      if (chatSearch.trim()) return;
      const channelId = update["0"]?.message?.conViewModel?.channelId;
      if (channelId !== null) return;

      const conv = update["0"].message.conViewModel;
      const isIncoming = update["0"].message.isOwner === false;
      const isOpen = selectedChatRef.current === conv.id;

      const updatedChat = mapChatItem({
        id:               conv.id,
        name:             conv.name,
        lastUpdated:      conv.lastUpdated,
        lastMessageDate:  conv.lastMessageDate,
        lastIncomingDate: conv.lastIncomingDate,
        type:             conv.type,
        text:             conv.text,
        messageType:      update["0"].messageType,
        unreadCount:      isOpen ? 0 : conv.unreadCount,
        isStar:           conv.isStar,
        isBlocked:        conv.isBlocked,
        whatsappId:       conv.whatsappId,
        contactId:        conv.contactId,
        activeTicketType: conv.activeTicketType,
        ticket:           conv.ticket,
        replyDeadline:    conv.replyDeadline,
        channelName:      conv.channelName,
      });

      setActiveChats(prev => {
        const exists = prev.some(c => c.id === updatedChat.id);
        const next = exists
          ? prev.map(c => c.id === updatedChat.id ? updatedChat : c)
          : [...prev, updatedChat];
        return [...next].sort((a, b) => {
          const aT = a.last_message_time?.isValid() ? a.last_message_time.valueOf() : 0;
          const bT = b.last_message_time?.isValid() ? b.last_message_time.valueOf() : 0;
          return bT - aT;
        });
      });

      if (isIncoming) {
        setFlashingChatIds(prev => new Set([...prev, conv.id]));
        setTimeout(() => setFlashingChatIds(prev => { const n = new Set(prev); n.delete(conv.id); return n; }), 2000);
      }
    });
  }, [watiChatUpdates]);

  const grouped = useMemo(() => groupQueriesByStatus(querySet), [querySet]);

  const fetchQueries = async (from = startDate, to = endDate) => {
    setLoading(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/whatsapp/get_querysets",
        {
          from: dayjs(from).format("YYYY-MM-DD"),
          to: dayjs(to).format("YYYY-MM-DD"),
          id: authState.id,
        },
        head
      );
      if (res.data.success) {
        setQuerySet(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch queries:", err);
      message.error("Failed to load queries. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchActiveChats = async (search = chatSearch, filter = chatSearchFilter) => {
    setChatsLoading(true);
    setHasMoreChats(true);
    try {
      const res = await axios.post(CHAT_API, { channelIds: "", search_filter: filter, search: search.trim() }, head);
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        setActiveChats(raw.map(mapChatItem));
        if (raw.length < 100) setHasMoreChats(false);
      }
    } catch {
      message.error("Failed to load active chats.");
    } finally {
      setChatsLoading(false);
    }
  };

  const loadMoreChats = async () => {
    if (chatsLoadingMore || !hasMoreChats || chatsLoading) return;
    setChatsLoadingMore(true);
    try {
      const lastId = activeChats[activeChats.length - 1]?.id;
      const res = await axios.post(CHAT_API, { channelIds: "", search_filter: chatSearchFilter, search: chatSearch.trim(), lastId }, head);
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        if (raw.length === 0) {
          setHasMoreChats(false);
        } else {
          setActiveChats(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const newItems = raw.map(mapChatItem).filter(c => !existingIds.has(c.id));
            return [...prev, ...newItems];
          });
          if (raw.length < 100) setHasMoreChats(false);
        }
      }
    } catch {
      // silent — don't interrupt the UX on pagination failure
    } finally {
      setChatsLoadingMore(false);
    }
  };

  useEffect(() => { fetchActiveChats(); }, []); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => fetchActiveChats(), 600);
    return () => clearTimeout(t);
  }, [chatSearch, chatSearchFilter]); // eslint-disable-line

  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    axios.get("https://cdn.jsdelivr.net/gh/avto-dev/vehicle-logotypes@2.x/src/vehicle-logotypes.json")
      .then(res => setCarLogoTypes(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    let isDown = false, dragging = false, startX = 0, startScrollLeft = 0, velX = 0, lastX = 0, animId = null;
    const THRESHOLD = 6;

    const onDown = (e) => {
      if (e.button !== 0) return;
      if (e.target.closest(".ant-btn, .ant-switch, input, .ant-select, .ant-input, .ant-tag")) return;
      isDown = true; dragging = false;
      startX = e.pageX; startScrollLeft = el.scrollLeft; velX = 0; lastX = e.pageX;
      cancelAnimationFrame(animId);
    };

    const onMove = (e) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      if (!dragging) {
        if (Math.abs(dx) < THRESHOLD) return;
        dragging = true;
        el.style.cursor = "grabbing";
      }
      e.preventDefault();
      velX = e.pageX - lastX;
      lastX = e.pageX;
      el.scrollLeft = startScrollLeft - dx;
    };

    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = "grab";
      if (!dragging) return;
      dragging = false;
      // Swallow the click that fires after mouseup so cards don't open
      document.addEventListener("click", e => { e.stopPropagation(); e.preventDefault(); }, { capture: true, once: true });
      const momentum = () => {
        if (Math.abs(velX) < 0.5) return;
        el.scrollLeft -= velX;
        velX *= 0.88;
        animId = requestAnimationFrame(momentum);
      };
      momentum();
    };

    el.addEventListener("mousedown", onDown);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      el.removeEventListener("mousedown", onDown);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      cancelAnimationFrame(animId);
    };
  }, []);

  const onDateChange = (dates) => {
    if (!dates) return;
    setStartDate(dates[0]);
    setEndDate(dates[1]);
    fetchQueries(dates[0], dates[1]);
  };

  const getAccordionKeys = (col, cards) => {
    if (col.placeholder) return [];
    if (groupByAgent) return [...new Set(cards.map(q => q.user_id || "Unassigned"))].sort();
    if (col.key === "confirmed") return ["paid", "pending"];
    if (col.key === "delivery") return ["out", "delivered"];
    return [];
  };

  const staleTimestamp = (q) => {
    const last = q?.timeline?.length > 0 ? q.timeline[q.timeline.length - 1] : null;
    if (!last) return Infinity;
    const t = dayjs(Object.values(last)[0], "YYYY-MM-DD hh:mm:ss A");
    return t.isValid() ? t.valueOf() : Infinity;
  };

  const renderColumnCards = (cards, col) => {
    const sorted = staleSortOn
      ? [...cards].sort((a, b) => staleTimestamp(a) - staleTimestamp(b))
      : internationalSortOn
      ? [...cards].sort((a, b) => (isInternational(a) ? 0 : 1) - (isInternational(b) ? 0 : 1))
      : highValueSortOn
      ? [...cards].sort((a, b) => (b.price_given || 0) - (a.price_given || 0))
      : urgencySortOn
      ? [...cards].sort((a, b) => (b.urgency || 0) - (a.urgency || 0))
      : cards;

    const allKeys    = getAccordionKeys(col, sorted);
    const activeKeys = collapseStates[col.key] ?? allKeys;
    const handleChange = (keys) => setCollapseStates(prev => ({ ...prev, [col.key]: keys }));

    if (col.placeholder) {
      return (
        <div style={{ textAlign: "center", color: "#bfbfbf", marginTop: 40, fontSize: 13 }}>
          {col.placeholderNote}
        </div>
      );
    }

    if (groupByAgent) {
      if (sorted.length === 0) {
        return <div style={{ textAlign: "center", color: "#bfbfbf", marginTop: 40, fontSize: 13 }}>No queries</div>;
      }
      const agentGroups = sorted.reduce((acc, q) => {
        const agent = q.user_id || "Unassigned";
        if (!acc[agent]) acc[agent] = [];
        acc[agent].push(q);
        return acc;
      }, {});
      return (
        <Collapse
          activeKey={activeKeys}
          onChange={handleChange}
          size="small"
          style={{ background: "transparent", border: "none" }}
          items={allKeys.map(agent => ({
            key: agent,
            label: (
              <span style={{ fontWeight: 700, fontSize: 12 }}>
                {agent}&nbsp;
                <Badge count={agentGroups[agent].length} showZero style={{ backgroundColor: col.color }} />
              </span>
            ),
            children: (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {agentGroups[agent].map((q, idx) => (
                  <QueryCard
                    key={q.query_id}
                    query={q}
                    columnColor={col.color}
                    showReason={col.key === "lost"}
                    onCardClick={() => setSelectedQuery(q)}
                    onWaClick={() => setSelectedSupplierQuery(q)}
                    slNo={idx + 1}
                    carLogoTypes={carLogoTypes}
                  />
                ))}
              </div>
            ),
          }))}
        />
      );
    }

    if (col.key === "delivery") {
      const outCards       = sorted.filter(q => q.status === "Out for delivery");
      const deliveredCards = sorted.filter(q => q.status === "Delivered");
      return (
        <Collapse
          activeKey={activeKeys}
          onChange={handleChange}
          size="small"
          style={{ background: "transparent", border: "none" }}
          items={[
            {
              key: "out",
              label: (
                <span style={{ color: "#fa8c16", fontWeight: 700, fontSize: 12 }}>
                  Out for Delivery&nbsp;
                  <Badge count={outCards.length} showZero style={{ backgroundColor: "#fa8c16" }} />
                </span>
              ),
              children: (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {outCards.length === 0
                    ? <div style={{ color: "#bfbfbf", textAlign: "center", fontSize: 13, padding: "8px 0" }}>No queries</div>
                    : outCards.map((q, idx) => (
                        <QueryCard key={q.query_id} query={q} columnColor="#fa8c16" showReason={false} onCardClick={() => setSelectedQuery(q)} onWaClick={() => setSelectedSupplierQuery(q)} slNo={idx + 1} carLogoTypes={carLogoTypes} />
                      ))
                  }
                </div>
              ),
            },
            {
              key: "delivered",
              label: (
                <span style={{ color: "#52c41a", fontWeight: 700, fontSize: 12 }}>
                  Delivered&nbsp;
                  <Badge count={deliveredCards.length} showZero style={{ backgroundColor: "#52c41a" }} />
                </span>
              ),
              children: (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {deliveredCards.length === 0
                    ? <div style={{ color: "#bfbfbf", textAlign: "center", fontSize: 13, padding: "8px 0" }}>No queries</div>
                    : deliveredCards.map((q, idx) => (
                        <QueryCard key={q.query_id} query={q} columnColor="#52c41a" showReason={false} onCardClick={() => setSelectedQuery(q)} onWaClick={() => setSelectedSupplierQuery(q)} slNo={idx + 1} carLogoTypes={carLogoTypes} />
                      ))
                  }
                </div>
              ),
            },
          ]}
        />
      );
    }

    if (col.key === "confirmed") {
      const paidCards    = sorted.filter(q => q.status === "Confirmed & Paid");
      const pendingCards = sorted.filter(q => q.status === "Confirmed");
      return (
        <Collapse
          activeKey={activeKeys}
          onChange={handleChange}
          size="small"
          style={{ background: "transparent", border: "none" }}
          items={[
            {
              key: "paid",
              label: (
                <span style={{ color: "#389e0d", fontWeight: 700, fontSize: 12 }}>
                  Paid&nbsp;
                  <Badge count={paidCards.length} showZero style={{ backgroundColor: "#389e0d" }} />
                </span>
              ),
              children: (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {paidCards.length === 0
                    ? <div style={{ color: "#bfbfbf", textAlign: "center", fontSize: 13, padding: "8px 0" }}>No paid queries</div>
                    : paidCards.map((q, idx) => (
                        <QueryCard key={q.query_id} query={q} columnColor="#389e0d" showReason={false} onCardClick={() => setSelectedQuery(q)} onWaClick={() => setSelectedSupplierQuery(q)} slNo={idx + 1} carLogoTypes={carLogoTypes} />
                      ))
                  }
                </div>
              ),
            },
            {
              key: "pending",
              label: (
                <span style={{ color: "#fa8c16", fontWeight: 700, fontSize: 12 }}>
                  Payment Pending&nbsp;
                  <Badge count={pendingCards.length} showZero style={{ backgroundColor: "#fa8c16" }} />
                </span>
              ),
              children: (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {pendingCards.length === 0
                    ? <div style={{ color: "#bfbfbf", textAlign: "center", fontSize: 13, padding: "8px 0" }}>No pending queries</div>
                    : pendingCards.map((q, idx) => (
                        <QueryCard key={q.query_id} query={q} columnColor="#fa8c16" showReason={false} onCardClick={() => setSelectedQuery(q)} onWaClick={() => setSelectedSupplierQuery(q)} slNo={idx + 1} carLogoTypes={carLogoTypes} />
                      ))
                  }
                </div>
              ),
            },
          ]}
        />
      );
    }

    if (sorted.length === 0) {
      return <div style={{ textAlign: "center", color: "#bfbfbf", marginTop: 40, fontSize: 13 }}>No queries</div>;
    }

    return sorted.map((query, idx) => (
      <QueryCard
        key={query.query_id}
        query={query}
        columnColor={col.color}
        showReason={col.key === "lost"}
        onCardClick={() => setSelectedQuery(query)}
        onWaClick={() => setSelectedSupplierQuery(query)}
        slNo={idx + 1}
        carLogoTypes={carLogoTypes}
      />
    ));
  };

  return (
    <div style={{ height: "calc(100vh - 100px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 16px",
          borderBottom: "1px solid #f0f0f0",
          backgroundColor: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 100,
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 16 }}>Sales Pipeline</span>
        <RangePicker
          value={[startDate, endDate]}
          onChange={onDateChange}
          format="DD-MM-YYYY"
          allowClear={false}
        />
        <Button icon={<ReloadOutlined />} onClick={() => fetchQueries()} loading={loading}>
          Reload
        </Button>
        <Badge
          count={querySet.length}
          overflowCount={99999}
          style={{ backgroundColor: "#1677ff" }}
          showZero
        />
        <span style={{ color: "#8c8c8c", fontSize: 13 }}>total queries</span>
        <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Switch size="small" checked={groupByAgent} onChange={setGroupByAgent} />
            <span style={{ fontSize: 13, color: groupByAgent ? "#1677ff" : "#8c8c8c", fontWeight: groupByAgent ? 600 : 400 }}>
              Group by Agent
            </span>
          </div>
          {/* Sort buttons — single row */}
          <div style={{ display: "flex", gap: 4 }}>
            <Tooltip title="Stale First" placement="bottom">
              <Button icon={<SortDescendingOutlined />} type={staleSortOn ? "primary" : "default"} onClick={() => setStaleSortOn(v => !v)} size="small" style={{ padding: "0 10px" }} />
            </Tooltip>
            <Tooltip title="International First" placement="bottom">
              <Button icon={<GlobalOutlined />} type={internationalSortOn ? "primary" : "default"} onClick={() => setInternationalSortOn(v => !v)} size="small" style={{ padding: "0 10px" }} />
            </Tooltip>
            <Tooltip title="High Value First" placement="bottom">
              <Button icon={<DollarOutlined />} type={highValueSortOn ? "primary" : "default"} onClick={() => setHighValueSortOn(v => !v)} size="small" style={{ padding: "0 10px" }} />
            </Tooltip>
            <Tooltip title="Urgent First" placement="bottom">
              <Button icon={<FireOutlined />} type={urgencySortOn ? "primary" : "default"} onClick={() => setUrgencySortOn(v => !v)} size="small" style={{ padding: "0 10px" }} danger={urgencySortOn} />
            </Tooltip>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
        {loading && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 50,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(255,255,255,0.7)",
          }}>
            <Spin size="large" />
          </div>
        )}
          <div
            ref={boardRef}
            style={{
              display: "flex", flexDirection: "row",
              gap: 12,
              paddingTop: 12, paddingBottom: 12, paddingLeft: 16,
              paddingRight: chatDrawerPinned && selectedChat ? chatDrawerWidth + 16 : 16,
              overflowX: "auto", overflowY: "hidden",
              height: "100%", alignItems: "flex-start",
              cursor: "grab", WebkitOverflowScrolling: "touch",
              userSelect: "none", WebkitUserSelect: "none",
            }}
          >
            {PIPELINE_COLUMNS.map((col) => {
              const cards   = grouped[col.key] || [];
              const allKeys = getAccordionKeys(col, cards);
              const activeKeys = collapseStates[col.key] ?? allKeys;
              const allOpen = allKeys.length > 0 && allKeys.every(k => activeKeys.includes(k));
              return (
                <div
                  key={col.key}
                  style={{
                    minWidth: 280,
                    maxWidth: 280,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    backgroundColor: "#fff",
                    borderRadius: 8,
                    border: `2px solid ${col.color}33`,
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {/* Column header */}
                  <div
                    style={{
                      padding: "10px 12px",
                      backgroundColor: col.color + "18",
                      borderBottom: `2px solid ${col.color}`,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: 13, color: col.color, display: "flex", alignItems: "center", gap: 6 }}>
                      {col.icon}
                      {col.label}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {allKeys.length > 0 && (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, fontSize: 11, height: "auto", color: col.color, opacity: 0.8 }}
                          onClick={() => setCollapseStates(prev => ({
                            ...prev,
                            [col.key]: allOpen ? [] : allKeys,
                          }))}
                        >
                          {allOpen ? "Collapse All" : "Expand All"}
                        </Button>
                      )}
                      <Badge
                        count={col.isChat ? activeChats.filter(c => c.unread_count > 0).length : cards.length}
                        overflowCount={9999}
                        showZero
                        style={{ backgroundColor: col.color }}
                      />
                    </div>
                  </div>

                  {/* Search bar for Active Chats column */}
                  {col.isChat && (
                    <>
                      <div style={{ padding: "6px 8px", flexShrink: 0, borderBottom: "1px solid #f0f0f0", background: "#fff" }}>
                        <Input size="small"
                          placeholder={chatSearchFilter === 0 ? "Search by name or number…" : "Search in messages…"}
                          value={chatSearch} onChange={e => setChatSearch(e.target.value)}
                          onPressEnter={() => fetchActiveChats()} allowClear
                          prefix={
                            <Select value={chatSearchFilter} onChange={val => setChatSearchFilter(val)} size="small" bordered={false} style={{ marginLeft: -7, marginRight: 2, fontSize: 11, minWidth: 78 }} dropdownStyle={{ minWidth: 130 }}>
                              <Select.Option value={0}>Name/No.</Select.Option>
                              <Select.Option value={1}>Messages</Select.Option>
                            </Select>
                          }
                          suffix={<SearchOutlined style={{ color: "#8c8c8c", cursor: "pointer" }} onClick={() => fetchActiveChats()} />}
                          style={{ borderRadius: 6, fontSize: 12 }}
                        />
                      </div>
                      <div style={{ padding: "4px 8px", flexShrink: 0, borderBottom: "1px solid #f0f0f0", background: "#fff", display: "flex", alignItems: "center", gap: 5 }}>
                        <Tooltip title={starredFilter ? "Show all chats" : "Show starred only"}>
                          <span
                            onClick={() => setStarredFilter(v => !v)}
                            style={{ cursor: "pointer", color: starredFilter ? "#faad14" : "#bfbfbf", fontSize: 13, display: "flex", alignItems: "center", transition: "color 0.2s", marginRight: 2 }}
                          >
                            {starredFilter ? <StarFilled /> : <StarOutlined />}
                          </span>
                        </Tooltip>
                        {[{ key: 1, label: "Open", color: "#389e0d", bg: "#f6ffed", border: "#52c41a" }, { key: 3, label: "Pending", color: "#d46b08", bg: "#fff7e6", border: "#ffa940" }, { key: 2, label: "Closed", color: "#0958d9", bg: "#e6f4ff", border: "#91caff" }].map(s => (
                          <span
                            key={s.key}
                            onClick={() => setStatusFilter(v => v === s.key ? null : s.key)}
                            style={{
                              cursor: "pointer", fontSize: 10, padding: "1px 6px", borderRadius: 10,
                              border: `1px solid ${statusFilter === s.key ? s.border : "#d9d9d9"}`,
                              color: statusFilter === s.key ? s.color : "#8c8c8c",
                              background: statusFilter === s.key ? s.bg : "transparent",
                              fontWeight: statusFilter === s.key ? 600 : 400,
                              transition: "all 0.15s", userSelect: "none",
                            }}
                          >
                            {s.label}
                          </span>
                        ))}
                        <Tooltip title={expirySortOn ? "Remove expiry sort" : "Sort by expiry (soonest first)"}>
                          <span
                            onClick={() => setExpirySortOn(v => !v)}
                            style={{
                              cursor: "pointer", fontSize: 10, padding: "1px 6px", borderRadius: 10,
                              border: `1px solid ${expirySortOn ? "#ff4d4f" : "#d9d9d9"}`,
                              color: expirySortOn ? "#ff4d4f" : "#8c8c8c",
                              background: expirySortOn ? "#fff1f0" : "transparent",
                              fontWeight: expirySortOn ? 600 : 400,
                              transition: "all 0.15s", userSelect: "none",
                              display: "inline-flex", alignItems: "center", gap: 3,
                            }}
                          >
                            <ClockCircleOutlined style={{ fontSize: 9 }} />↓
                          </span>
                        </Tooltip>
                        <span style={{ fontSize: 10, color: "#8c8c8c", flexShrink: 0, marginLeft: "auto" }}>
                          {(() => {
                            let list = activeChats;
                            if (starredFilter) list = list.filter(c => c.starred);
                            if (statusFilter !== null) list = list.filter(c => c.ticket_status === statusFilter);
                            return list.length;
                          })()} shown
                        </span>
                      </div>
                    </>
                  )}

                  {/* Cards scroll area */}
                  <div
                    ref={col.isChat ? chatScrollRef : null}
                    onScroll={col.isChat ? (e => {
                      const el = e.currentTarget;
                      if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) loadMoreChats();
                    }) : null}
                    style={{ flex: 1, overflowY: "auto", padding: col.isChat ? 8 : 8, display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {col.isChat ? (
                      chatsLoading
                        ? <div style={{ display: "flex", justifyContent: "center", paddingTop: 32 }}><Spin /></div>
                        : (() => {
                            let visibleChats = activeChats;
                            if (starredFilter) visibleChats = visibleChats.filter(c => c.starred);
                            if (statusFilter !== null) visibleChats = visibleChats.filter(c => c.ticket_status === statusFilter);
                            if (expirySortOn) visibleChats = [...visibleChats].sort((a, b) => {
                              const aMs = a.expiry?.isValid() ? a.expiry.valueOf() : Infinity;
                              const bMs = b.expiry?.isValid() ? b.expiry.valueOf() : Infinity;
                              return bMs - aMs;
                            });
                            return visibleChats.length === 0
                              ? <div style={{ textAlign: "center", color: "#bfbfbf", marginTop: 40, fontSize: 13 }}>No chats</div>
                              : (
                                <>
                                  <AnimatePresence initial={false}>
                                    {visibleChats.map(chat => (
                                      <motion.div
                                        key={chat.id}
                                        layout
                                        initial={{ opacity: 0, y: -12 }}
                                        animate={{
                                          opacity: 1, y: 0,
                                          boxShadow: flashingChatIds.has(chat.id) ? "0 0 0 2px #00a884" : "none",
                                        }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ layout: { duration: 0.28, ease: "easeOut" }, opacity: { duration: 0.18 }, boxShadow: { duration: 0.3 } }}
                                        style={{ borderRadius: 8 }}
                                      >
                                        <ActiveChatCard chat={chat} isSelected={selectedChat?.id === chat.id} onClick={() => setSelectedChat(chat)} />
                                      </motion.div>
                                    ))}
                                  </AnimatePresence>
                                  {chatsLoadingMore && <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}><Spin size="small" /></div>}
                                  {!hasMoreChats && activeChats.length > 0 && <div style={{ textAlign: "center", fontSize: 11, color: "#8c8c8c", padding: "6px 0" }}>All chats loaded</div>}
                                </>
                              );
                          })()
                    ) : (
                      renderColumnCards(cards, col)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
      </div>

      <DetailModal query={selectedQuery} onClose={() => setSelectedQuery(null)} />
      <SuppliersModal query={selectedSupplierQuery} onClose={() => setSelectedSupplierQuery(null)} />
      <ActiveChatDrawer
        chat={selectedChat}
        pinned={chatDrawerPinned}
        onPinToggle={() => setChatDrawerPinned(v => !v)}
        onClose={() => setSelectedChat(null)}
        onWidthChange={setChatDrawerWidth}
        onChatPatch={fields => {
          if (!selectedChat) return;
          const patch = c => c.id === selectedChat.id ? { ...c, ...fields } : c;
          setActiveChats(cs => cs.map(patch));
          setSelectedChat(c => patch(c));
        }}
      />
    </div>
  );
}

export default QuerysetV4;
